const API_URL = process.env.REACT_APP_BACKEND_URL;
// Re-exported so other utils (e.g. pwaManifest) can build absolute URLs
// against the same backend origin without redefining the env lookup.
export { API_URL };

// ---------------------------------------------------------------------------
// Global 402 (SUBSCRIPTION_LOCKED) interceptor
// ---------------------------------------------------------------------------
//
// When a nursery's trial ends and its 14-day grace period is over, backend
// writes return HTTP 402 with a machine-readable payload:
//     { detail: { error_code: "SUBSCRIPTION_LOCKED", state, message } }
//
// Rather than sprinkling handling across every API caller, we monkey-patch
// window.fetch once so any 402 from the backend surfaces a friendly toast
// and gently pushes the user to the billing page. The response is still
// returned unchanged so individual callers can decide what else to do.
if (typeof window !== 'undefined' && !window.__kw_402_hook_installed) {
  const _origFetch = window.fetch.bind(window);
  let _lastLockedToast = 0;
  window.fetch = async (input, init) => {
    const res = await _origFetch(input, init);
    if (res && res.status === 402) {
      try {
        // Clone so callers can still read the body.
        const cloned = res.clone();
        const payload = await cloned.json();
        const detail = payload && (payload.detail || payload);
        const code = detail && (detail.error_code || detail.code);
        if (code === 'SUBSCRIPTION_LOCKED') {
          const now = Date.now();
          // Throttle so bursts of parallel writes don't spam toasts
          if (now - _lastLockedToast > 4000) {
            _lastLockedToast = now;
            const state = detail.state || 'grace';
            const msg = state === 'locked'
              ? 'Workspace is locked — reactivate to make changes.'
              : 'Trial ended — you\'re in read-only mode. Reactivate to make changes.';
            try {
              // Dynamic import so this file stays cheap for callers that
              // don't need toasts (e.g. server-side rendering scenarios).
              const { toast } = await import('sonner');
              toast.warning(msg, {
                action: {
                  label: 'Reactivate',
                  onClick: () => {
                    try {
                      window.location.href = '/admin?tab=billing';
                    } catch (_) { /* noop */ }
                  },
                },
                duration: 6000,
              });
            } catch (_) { /* toasts optional */ }
          }
        }
      } catch (_) { /* body not JSON — ignore */ }
    }
    return res;
  };
  window.__kw_402_hook_installed = true;
}

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export const api = {
  // Auth
  login: async (email, password) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);
    
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      body: formData
    });
    
    if (!res.ok) throw new Error('Login failed');
    return res.json();
  },
  
  register: async (userData) => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });
    
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Registration failed' }));
      throw new Error(err.detail || 'Registration failed');
    }
    return res.json();
  },
  
  // Organization registration (new company signup)
  registerOrganization: async (orgData) => {
    const res = await fetch(`${API_URL}/api/organizations/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(orgData)
    });
    
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Organization registration failed' }));
      throw new Error(err.detail || 'Organization registration failed');
    }
    return res.json();
  },
  
  getMyOrganization: async () => {
    const res = await fetch(`${API_URL}/api/organizations/me`, {
      headers: getAuthHeader()
    });
    if (!res.ok) throw new Error('Failed to fetch organization');
    return res.json();
  },
  
  updateMyOrganization: async (updates) => {
    const res = await fetch(`${API_URL}/api/organizations/me`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to update organization' }));
      throw new Error(err.detail || 'Failed to update organization');
    }
    return res.json();
  },
  
  getOrganizationCenters: async () => {
    const res = await fetch(`${API_URL}/api/organizations/centers`, {
      headers: getAuthHeader()
    });
    if (!res.ok) throw new Error('Failed to fetch centers');
    return res.json();
  },
  
  addOrganizationCenter: async (centerData) => {
    const res = await fetch(`${API_URL}/api/organizations/centers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(centerData)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to add center' }));
      throw new Error(err.detail || 'Failed to add center');
    }
    return res.json();
  },

  uploadOrganizationLogo: async (file) => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${API_URL}/api/organizations/me/logo`, {
      method: 'POST',
      headers: { ...getAuthHeader() }, // do NOT set Content-Type; browser sets multipart boundary
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to upload logo' }));
      throw new Error(err.detail || 'Failed to upload logo');
    }
    return res.json();
  },

  removeOrganizationLogo: async () => {
    const res = await fetch(`${API_URL}/api/organizations/me/logo`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to remove logo' }));
      throw new Error(err.detail || 'Failed to remove logo');
    }
    return res.json();
  },

  // ============================================================================
  // Generic File Service — S3-backed uploads for moments, documents, etc.
  // ============================================================================
  uploadFile: async (file, { category = 'generic', linkedType, linkedId } = {}) => {
    const form = new FormData();
    form.append('file', file);
    const qs = new URLSearchParams({ category });
    if (linkedType) qs.append('linked_type', linkedType);
    if (linkedId) qs.append('linked_id', linkedId);
    const res = await fetch(`${API_URL}/api/files/upload?${qs}`, {
      method: 'POST',
      headers: { ...getAuthHeader() }, // browser sets multipart boundary
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(err.detail || 'Upload failed');
    }
    return res.json();
  },

  getFile: async (fileId) => {
    const res = await fetch(`${API_URL}/api/files/${fileId}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('File not found');
    return res.json();
  },

  deleteFile: async (fileId) => {
    const res = await fetch(`${API_URL}/api/files/${fileId}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Delete failed' }));
      throw new Error(err.detail || 'Delete failed');
    }
    return res.json();
  },
  
  // ====== Super Admin (platform-wide) ======
  getSuperAdminStats: async () => {
    const res = await fetch(`${API_URL}/api/super-admin/stats`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to fetch platform stats');
    return res.json();
  },
  
  getSuperAdminOrganizations: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.is_active !== undefined && params.is_active !== null && params.is_active !== '') qs.set('is_active', params.is_active);
    if (params.search) qs.set('search', params.search);
    const url = `${API_URL}/api/super-admin/organizations${qs.toString() ? '?' + qs.toString() : ''}`;
    const res = await fetch(url, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to fetch organizations');
    return res.json();
  },
  
  getSuperAdminOrganizationDetail: async (orgId) => {
    const res = await fetch(`${API_URL}/api/super-admin/organizations/${orgId}`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to fetch organization detail');
    return res.json();
  },
  
  superAdminUpdateOrganization: async (orgId, updates) => {
    const res = await fetch(`${API_URL}/api/super-admin/organizations/${orgId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to update organization' }));
      throw new Error(err.detail || 'Failed to update organization');
    }
    return res.json();
  },
  
  superAdminSuspendOrganization: async (orgId) => {
    const res = await fetch(`${API_URL}/api/super-admin/organizations/${orgId}/suspend`, {
      method: 'POST',
      headers: getAuthHeader()
    });
    if (!res.ok) throw new Error('Failed to suspend organization');
    return res.json();
  },
  
  superAdminActivateOrganization: async (orgId) => {
    const res = await fetch(`${API_URL}/api/super-admin/organizations/${orgId}/activate`, {
      method: 'POST',
      headers: getAuthHeader()
    });
    if (!res.ok) throw new Error('Failed to activate organization');
    return res.json();
  },
  
  superAdminDeleteOrganization: async (orgId) => {
    const res = await fetch(`${API_URL}/api/super-admin/organizations/${orgId}?confirm=true`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to delete organization' }));
      throw new Error(err.detail || 'Failed to delete organization');
    }
    return res.json();
  },

  /**
   * Super-admin escape hatch: reset any user's password.
   * Pass `newPassword` for a specific value; omit it to have the server
   * auto-generate a strong temporary password and return it.
   */
  superAdminResetUserPassword: async (userId, newPassword = null) => {
    const body = newPassword ? { new_password: newPassword } : {};
    const res = await fetch(`${API_URL}/api/super-admin/users/${userId}/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to reset password' }));
      throw new Error(err.detail || 'Failed to reset password');
    }
    return res.json();
  },

  // ============================================================
  // Super Admin Command Center (Phase K) — Support + Revenue
  // ============================================================

  /** Issue a scoped impersonation token for `userId` (2h TTL). */
  superAdminImpersonate: async (userId) => {
    const res = await fetch(`${API_URL}/api/super-admin/impersonate/${userId}`, {
      method: 'POST', headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to start impersonation' }));
      throw new Error(err.detail || 'Failed to start impersonation');
    }
    return res.json();
  },

  /** Detailed billing snapshot for one org (subscription + invoices + payments). */
  superAdminOrgBilling: async (orgId) => {
    const res = await fetch(`${API_URL}/api/super-admin/organizations/${orgId}/billing`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to load billing');
    return res.json();
  },

  /** Compact per-org usage summary (counts + storage bytes). */
  superAdminOrgUsage: async (orgId) => {
    const res = await fetch(`${API_URL}/api/super-admin/organizations/${orgId}/usage`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to load usage');
    return res.json();
  },

  /** Add N days to an org's trial. */
  superAdminExtendTrial: async (orgId, days) => {
    const res = await fetch(`${API_URL}/api/super-admin/organizations/${orgId}/extend-trial`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ days }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to extend trial' }));
      throw new Error(err.detail || 'Failed to extend trial');
    }
    return res.json();
  },

  /** Force-set an org's plan/interval/status. Any field omitted is left alone. */
  superAdminSetSubscription: async (orgId, payload) => {
    const res = await fetch(`${API_URL}/api/super-admin/organizations/${orgId}/set-subscription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload || {}),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to update subscription' }));
      throw new Error(err.detail || 'Failed to update subscription');
    }
    return res.json();
  },

  /** Resend the org welcome email to the owner. */
  superAdminResendOrgWelcome: async (orgId) => {
    const res = await fetch(`${API_URL}/api/admin/organizations/${orgId}/resend-welcome`, {
      method: 'POST', headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to resend welcome' }));
      throw new Error(err.detail || 'Failed to resend welcome');
    }
    return res.json();
  },

  /** Filterable + paginated audit log query. */
  superAdminAuditLog: async ({ orgId, actor, action, fromTs, toTs, page = 1, pageSize = 50 } = {}) => {
    const qs = new URLSearchParams();
    if (orgId) qs.set('org_id', orgId);
    if (actor) qs.set('actor', actor);
    if (action) qs.set('action', action);
    if (fromTs) qs.set('from_ts', fromTs);
    if (toTs) qs.set('to_ts', toTs);
    qs.set('page', page);
    qs.set('page_size', pageSize);
    const res = await fetch(`${API_URL}/api/super-admin/audit-log?${qs.toString()}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to load audit log');
    return res.json();
  },

  /** Distinct action names in the audit log (for the filter dropdown). */
  superAdminAuditActions: async () => {
    const res = await fetch(`${API_URL}/api/super-admin/audit-log/actions`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to load actions');
    return res.json();
  },

  /** Global search across orgs/users/children. */
  superAdminGlobalSearch: async (q, limit = 8) => {
    const qs = new URLSearchParams({ q, limit: String(limit) });
    const res = await fetch(`${API_URL}/api/super-admin/search?${qs.toString()}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Search failed');
    return res.json();
  },

  /** Platform revenue snapshot — MRR/ARR, plan mix, 90d signup series. */
  superAdminRevenue: async () => {
    const res = await fetch(`${API_URL}/api/super-admin/analytics/revenue`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to load revenue analytics');
    return res.json();
  },

  /** Cohort funnel: signup → onboarded → trialing → paid over N days. */
  superAdminCohorts: async (window = 30) => {
    const res = await fetch(`${API_URL}/api/super-admin/analytics/cohorts?window=${window}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to load cohort funnel');
    return res.json();
  },

  /** Orgs whose owner hasn't logged in for the last N days. */
  superAdminInactiveOrgs: async (days = 30) => {
    const res = await fetch(`${API_URL}/api/super-admin/inactive-organizations?days=${days}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to load inactive orgs');
    return res.json();
  },

  /** Platform-wide email deliverability health for the last N days. */
  superAdminEmailHealth: async (days = 7) => {
    const res = await fetch(`${API_URL}/api/super-admin/email/health?days=${days}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to load email health');
    return res.json();
  },

  /**
   * Authenticated user changes their own password (requires current password).
   */
  changeMyPassword: async (currentPassword, newPassword) => {
    const res = await fetch(`${API_URL}/api/users/me/change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to change password' }));
      throw new Error(err.detail || 'Failed to change password');
    }
    return res.json();
  },

  /** Upload/replace the calling user's profile photo (used on ID card). */
  uploadMyAvatar: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_URL}/api/me/avatar`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to upload photo' }));
      throw new Error(err.detail || 'Failed to upload photo');
    }
    return res.json();
  },

  /** Remove the calling user's avatar (revert to initial fallback). */
  deleteMyAvatar: async () => {
    const res = await fetch(`${API_URL}/api/me/avatar`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to remove photo' }));
      throw new Error(err.detail || 'Failed to remove photo');
    }
    return res.json();
  },

  /** Admin: upload a profile photo on behalf of a team member. */
  uploadUserAvatar: async (userId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_URL}/api/users/${userId}/avatar`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to upload photo' }));
      throw new Error(err.detail || 'Failed to upload photo');
    }
    return res.json();
  },

  /** Admin: clear a team member's profile photo (also purges pending). */
  deleteUserAvatar: async (userId) => {
    const res = await fetch(`${API_URL}/api/users/${userId}/avatar`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to remove photo' }));
      throw new Error(err.detail || 'Failed to remove photo');
    }
    return res.json();
  },

  /** Admin: set a specific password on a user (direct, no email, no forced rotation). */
  adminSetUserPassword: async (userId, newPassword) => {
    const res = await fetch(`${API_URL}/api/users/${userId}/set-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ new_password: newPassword }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to set password' }));
      throw new Error(err.detail || 'Failed to set password');
    }
    return res.json();
  },

  /** Admin: generate a temp password, email it, and force change on next login. */
  adminResetUserPassword: async (userId) => {
    const res = await fetch(`${API_URL}/api/users/${userId}/reset-password`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to reset password' }));
      throw new Error(err.detail || 'Failed to reset password');
    }
    return res.json();
  },

  /** Super Admin: list every demo request (newest-first, capped at 500). */
  getDemoRequests: async () => {
    const res = await fetch(`${API_URL}/api/admin/demo-requests`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to load demo requests');
    return res.json();
  },

  /** Super Admin: move a demo request through the pipeline. */
  updateDemoRequestStatus: async (id, status, notes = null) => {
    const res = await fetch(`${API_URL}/api/admin/demo-requests/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ status, ...(notes ? { notes } : {}) }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to update status');
    }
    return res.json();
  },

  /** Super Admin: provision an organisation from a demo request (one-click handoff). */
  provisionDemoRequest: async (id, overrides = {}) => {
    const res = await fetch(`${API_URL}/api/admin/demo-requests/${id}/provision`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(overrides || {}),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to provision nursery');
    }
    return res.json();
  },

  /** Onboarding: fetch step status + wizard visibility flag. */
  getOnboardingStatus: async () => {
    const res = await fetch(`${API_URL}/api/onboarding/status`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to load onboarding status');
    return res.json();
  },
  /** Onboarding step 1: upload org logo. */
  uploadOnboardingLogo: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_URL}/api/onboarding/logo`, {
      method: 'POST', headers: getAuthHeader(), body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to upload logo');
    }
    return res.json();
  },
  /** Onboarding step 2: save primary center address. */
  saveOnboardingAddress: async (address) => {
    const res = await fetch(`${API_URL}/api/onboarding/address`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(address),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to save address');
    }
    return res.json();
  },
  /** Onboarding step 3: bulk-invite staff. */
  inviteOnboardingStaff: async (invites) => {
    const res = await fetch(`${API_URL}/api/onboarding/invite-staff`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ invites }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to send invites');
    }
    return res.json();
  },
  /** Onboarding: mark wizard finished / skipped. */
  completeOnboarding: async (skipped = false) => {
    const res = await fetch(`${API_URL}/api/onboarding/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ skipped }),
    });
    if (!res.ok) throw new Error('Failed to complete onboarding');
    return res.json();
  },

  /** Public: fetch per-day uptime for each service (default 30 days). */
  getStatusHistory: async (days = 30) => {
    const res = await fetch(`${API_URL}/api/status/history?days=${encodeURIComponent(days)}`);
    if (!res.ok) throw new Error('Failed to load status history');
    return res.json();
  },

  /** Admin: count of parent self-signups in the last 7 days (drives the nav badge). */
  getNewRegistrationsCount: async () => {
    const res = await fetch(`${API_URL}/api/admin/new-registrations-count`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) return { count: 0 };
    return res.json();
  },

  // ----- Staff ID cards -----

  /** Fetch (and lazily create) my own staff ID card with share token. */
  getMyStaffId: async () => {
    const res = await fetch(`${API_URL}/api/me/staff-id`, { headers: getAuthHeader() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to load ID card' }));
      throw new Error(err.detail || 'Failed to load ID card');
    }
    return res.json();
  },

  /** Re-issue my share token (invalidates the previously shared link). */
  rotateMyStaffId: async () => {
    const res = await fetch(`${API_URL}/api/me/staff-id/rotate`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to rotate ID' }));
      throw new Error(err.detail || 'Failed to rotate ID');
    }
    return res.json();
  },

  /** Admin: revoke another user's ID card (they leave / dismissed). */
  revokeStaffId: async (userId) => {
    const res = await fetch(`${API_URL}/api/users/${userId}/staff-id/revoke`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to revoke ID' }));
      throw new Error(err.detail || 'Failed to revoke ID');
    }
    return res.json();
  },

  /** Admin: reverse a previous revoke. */
  restoreStaffId: async (userId) => {
    const res = await fetch(`${API_URL}/api/users/${userId}/staff-id/restore`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to restore ID' }));
      throw new Error(err.detail || 'Failed to restore ID');
    }
    return res.json();
  },

  /** Admin: view a specific staff member's ID card (badge preview + QR + share URL). */
  getStaffIdForUser: async (userId) => {
    const res = await fetch(`${API_URL}/api/users/${userId}/staff-id`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to load ID card' }));
      throw new Error(err.detail || 'Failed to load ID card');
    }
    return res.json();
  },

  /** Admin: rotate a specific staff member's share token (invalidates previous QR/link). */
  rotateStaffIdForUser: async (userId) => {
    const res = await fetch(`${API_URL}/api/users/${userId}/staff-id/rotate`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to rotate ID' }));
      throw new Error(err.detail || 'Failed to rotate ID');
    }
    return res.json();
  },

  /** Public verify endpoint (no auth) — used by the QR-code landing page. */
  verifyStaffId: async (token) => {
    const res = await fetch(`${API_URL}/api/public/staff-id/verify/${encodeURIComponent(token)}`);
    if (res.status === 404) return { not_found: true };
    if (!res.ok) {
      throw new Error('Failed to verify ID card');
    }
    return res.json();
  },

  // ----- Photo approval queue (Phase 10 — Hybrid safeguarding workflow) -----

  /** Manager: list staff in my org with a profile photo awaiting approval. */
  listAvatarApprovals: async () => {
    const res = await fetch(`${API_URL}/api/admin/avatar-approvals`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to load approvals' }));
      throw new Error(err.detail || 'Failed to load approvals');
    }
    return res.json();
  },

  /** Manager: approve a pending photo → promotes it to the live ID card. */
  approveAvatar: async (userId) => {
    const res = await fetch(`${API_URL}/api/admin/avatar-approvals/${encodeURIComponent(userId)}/approve`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to approve photo' }));
      throw new Error(err.detail || 'Failed to approve photo');
    }
    return res.json();
  },

  /** Manager: reject a pending photo. Optionally include a reason emailed to staff. */
  rejectAvatar: async (userId, reason = '') => {
    const res = await fetch(`${API_URL}/api/admin/avatar-approvals/${encodeURIComponent(userId)}/reject`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason || '' }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to reject photo' }));
      throw new Error(err.detail || 'Failed to reject photo');
    }
    return res.json();
  },

  /**
   * Self-service "forgot password" — sends a reset link to the supplied
   * email. ALWAYS resolves successfully on the API level (the server
   * returns 200 even for unknown emails, to avoid account-enumeration).
   */
  forgotPassword: async (email) => {
    const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    // The server is deliberately uniform here; just parse and return.
    return res.json().catch(() => ({ status: 'ok' }));
  },

  /**
   * Pre-flight check on a reset token (UX nicety so we don't make the
   * user type a new password into a dead form).
   */
  validateResetToken: async (token) => {
    const res = await fetch(
      `${API_URL}/api/auth/reset-password/${encodeURIComponent(token)}`,
    );
    if (!res.ok) return { valid: false, reason: 'unreachable' };
    return res.json();
  },

  /**
   * Consume a reset token + set a brand new password.
   */
  resetPassword: async (token, newPassword) => {
    const res = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to reset password' }));
      throw new Error(err.detail || 'Failed to reset password');
    }
    return res.json();
  },

  /** List every account with role=super_admin (Platform Admins tab). */
  listPlatformSuperAdmins: async () => {
    const res = await fetch(`${API_URL}/api/super-admin/super-admins`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to load super admins' }));
      throw new Error(err.detail || 'Failed to load super admins');
    }
    return res.json();
  },

  /**
   * Super-admin creates any user, including another super_admin.
   * Body fields: email, password, full_name, role, phone?, organization_id?
   */
  superAdminCreateUser: async (payload) => {
    const res = await fetch(`${API_URL}/api/super-admin/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to create user' }));
      throw new Error(err.detail || 'Failed to create user');
    }
    return res.json();
  },

  superAdminDeactivateUser: async (userId) => {
    const res = await fetch(`${API_URL}/api/super-admin/users/${userId}/deactivate`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to deactivate user' }));
      throw new Error(err.detail || 'Failed to deactivate user');
    }
    return res.json();
  },

  superAdminActivateUser: async (userId) => {
    const res = await fetch(`${API_URL}/api/super-admin/users/${userId}/activate`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to activate user' }));
      throw new Error(err.detail || 'Failed to activate user');
    }
    return res.json();
  },

  // -------- User drill-down (admin-only) --------
  getUserChildren: async (userId) => {
    const res = await fetch(`${API_URL}/api/users/${userId}/children`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to load children');
    return res.json();
  },
  getUserEnrollments: async (userId) => {
    const res = await fetch(`${API_URL}/api/users/${userId}/enrollments`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to load enrollments');
    return res.json();
  },
  getUserBookings: async (userId, limit = 50) => {
    const res = await fetch(`${API_URL}/api/users/${userId}/bookings?limit=${limit}`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to load bookings');
    return res.json();
  },

  // -------- Daily attendance / chain of custody --------
  getTodayRoster: async ({ date, centerId } = {}) => {
    const qs = new URLSearchParams();
    if (date) qs.set('date', date);
    if (centerId) qs.set('center_id', centerId);
    const url = `${API_URL}/api/attendance/today${qs.toString() ? `?${qs}` : ''}`;
    const res = await fetch(url, { headers: getAuthHeader() });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to load roster');
    return res.json();
  },
  attendanceCheckIn: async (payload) => {
    const res = await fetch(`${API_URL}/api/attendance/check-in`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Check-in failed');
    return res.json();
  },
  attendanceCheckOut: async (payload) => {
    const res = await fetch(`${API_URL}/api/attendance/check-out`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Check-out failed');
    return res.json();
  },
  attendanceMarkAbsent: async (payload) => {
    const res = await fetch(`${API_URL}/api/attendance/mark-absent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Mark absent failed');
    return res.json();
  },
  
  // ====== Public branding (unauthenticated) ======
  getOrgBrandingBySlug: async (slug) => {
    const res = await fetch(`${API_URL}/api/organizations/by-slug/${encodeURIComponent(slug)}/branding`);
    if (!res.ok) throw new Error('Branding not found');
    return res.json();
  },
  
  // ====== Compliance ======
  /** Static catalogue of supported certificate kinds + labels. */
  getComplianceLabels: async () => {
    const res = await fetch(`${API_URL}/api/staff/compliance/labels`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to load compliance labels');
    return res.json();
  },
  /** Catalogue + records for the current user (staff dashboard). */
  getMyCompliance: async () => {
    const res = await fetch(`${API_URL}/api/staff/compliance/me`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to load compliance status');
    return res.json();
  },
  /** Admin-only org-wide rollup. */
  getComplianceSummary: async () => {
    const res = await fetch(`${API_URL}/api/staff/compliance/summary`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to load compliance summary');
    return res.json();
  },
  /** Admin: list records (optionally filter by staff_id). */
  listComplianceRecords: async (staffId) => {
    const qs = staffId ? `?staff_id=${encodeURIComponent(staffId)}` : '';
    const res = await fetch(`${API_URL}/api/staff/compliance/records${qs}`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to load compliance records');
    return res.json();
  },
  /** Create or upload a compliance record (staff: self, admin: any staff). */
  createComplianceRecord: async (payload) => {
    const res = await fetch(`${API_URL}/api/staff/compliance/records`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to create record');
    }
    return res.json();
  },
  updateComplianceRecord: async (id, updates) => {
    const res = await fetch(`${API_URL}/api/staff/compliance/records/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(updates),
    });
    if (!res.ok) throw new Error('Failed to update record');
    return res.json();
  },
  deleteComplianceRecord: async (id) => {
    const res = await fetch(`${API_URL}/api/staff/compliance/records/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to delete record');
    return res.json();
  },
  verifyComplianceRecord: async (id) => {
    const res = await fetch(`${API_URL}/api/staff/compliance/records/${id}/verify`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to verify record');
    return res.json();
  },
  rejectComplianceRecord: async (id, reason) => {
    const res = await fetch(`${API_URL}/api/staff/compliance/records/${id}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) throw new Error('Failed to reject record');
    return res.json();
  },

  lookupBrandingByEmail: async (email) => {
    const res = await fetch(`${API_URL}/api/organizations/lookup-branding?email=${encodeURIComponent(email)}`);
    if (!res.ok) throw new Error('Workspace not found');
    return res.json();
  },

  // ====== Terms & Conditions ======
  /**
   * Mark the current user as having read the latest org T&Cs version.
   * Triggered from the parent dashboard banner.
   */
  reacknowledgeTerms: async () => {
    const res = await fetch(`${API_URL}/api/users/me/reacknowledge-terms`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Could not record acknowledgement');
    return res.json();
  },

  /**
   * Build a token-authenticated URL that opens the printable / "Save as
   * PDF" view of an enrolment in a new tab. The token is appended as a
   * query string so `window.open()` works without setting headers.
   */
  getEnrollmentPrintableUrl: (enrollmentId) => {
    const token = localStorage.getItem('token') || '';
    const t = encodeURIComponent(token);
    const id = encodeURIComponent(enrollmentId);
    return `${API_URL}/api/enrollments/${id}/printable?token=${t}`;
  },
  
  getMe: async () => {
    const res = await fetch(`${API_URL}/api/auth/me`, {
      headers: getAuthHeader()
    });
    
    if (!res.ok) throw new Error('Not authenticated');
    return res.json();
  },
  
  // Children
  getChildren: async () => {
    const res = await fetch(`${API_URL}/api/children`, {
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  createChild: async (childData) => {
    const res = await fetch(`${API_URL}/api/children`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(childData)
    });
    return res.json();
  },
  
  // Sessions
  getSessionTemplates: async () => {
    const res = await fetch(`${API_URL}/api/sessions/templates`, {
      headers: getAuthHeader()
    });
    return res.json();
  },

  // Term calendar — UK term-time-aware session generation.
  getTerms: async () => {
    const res = await fetch(`${API_URL}/api/terms`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to load terms');
    return res.json();
  },
  createTerm: async (body) => {
    const res = await fetch(`${API_URL}/api/terms`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create term');
    return res.json();
  },
  updateTerm: async (id, body) => {
    const res = await fetch(`${API_URL}/api/terms/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update term');
    return res.json();
  },
  deleteTerm: async (id) => {
    const res = await fetch(`${API_URL}/api/terms/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to delete term');
    return res.json();
  },
  seedUkTerms: async (academicYear, overwrite = false, kind = 'term') => {
    const res = await fetch(`${API_URL}/api/terms/seed`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ academic_year: academicYear, overwrite, kind }),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to seed terms');
    return res.json();
  },
  /** Term-time session generation. preview=true returns dates only.
   *  Optional date_from / date_to (YYYY-MM-DD) clip the output without
   *  changing the underlying term windows. */
  generateTermTimeSessions: async ({ template_id, term_ids, weekdays_iso, preview_only = false, date_from = null, date_to = null }) => {
    const payload = { template_id, term_ids, weekdays_iso, preview_only };
    if (date_from) payload.date_from = date_from;
    if (date_to) payload.date_to = date_to;
    const res = await fetch(`${API_URL}/api/sessions/instances/generate-term-time`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to generate sessions');
    return res.json();
  },
  getClosedDatesWindow: async (start, end) => {
    const res = await fetch(`${API_URL}/api/terms/closed-dates?start=${start}&end=${end}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to load closed dates');
    return res.json();
  },

  createSessionTemplate: async (templateData) => {
    const res = await fetch(`${API_URL}/api/sessions/templates`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(templateData)
    });
    return res.json();
  },
  
  getSessionInstances: async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const res = await fetch(`${API_URL}/api/sessions/instances?${params}`, {
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  generateSessionInstances: async (templateId, startDate, endDate) => {
    const params = new URLSearchParams({
      template_id: templateId,
      start_date: startDate,
      end_date: endDate
    });
    
    const res = await fetch(`${API_URL}/api/sessions/instances/generate?${params}`, {
      method: 'POST',
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  // Bookings
  createBooking: async (bookingData) => {
    const res = await fetch(`${API_URL}/api/bookings`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(bookingData)
    });
    return res.json();
  },

  // Multi-child booking — books the same session for several kids in
  // one round-trip. Returns { total, confirmed[], waitlisted[], errors[],
  // total_charged }. The caller decides how to surface partial outcomes
  // (e.g. "2 confirmed, 1 waitlisted") in the UI.
  createBookingsBatch: async (bookingData) => {
    const res = await fetch(`${API_URL}/api/bookings/batch`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(bookingData),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Batch booking failed (${res.status})`);
    }
    return res.json();
  },

  // Bulk multi-session booking — books MANY sessions for one/more children
  // in a single round-trip. Supports `dry_run: true` which returns the
  // same shape but without writing any rows so the caller can render a
  // preview screen ("3 will book, 1 will waitlist, 2 invalid") before
  // asking the admin to confirm.
  //
  // Body:
  //   session_instance_ids: string[]   // 1..50
  //   child_ids:            string[]   // 1..20
  //   service_type:         string
  //   requires_transport:   boolean
  //   transport_requirements: object|null
  //   on_behalf_of_parent_id: string   // admin-only, optional
  //   dry_run:              boolean    // default false
  //
  // Response includes both flat arrays (confirmed / waitlisted / errors)
  // and a `by_session` map keyed by session_instance_id for grouped UI.
  createBookingsBulk: async (bookingData) => {
    const res = await fetch(`${API_URL}/api/bookings/bulk`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(bookingData),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Bulk booking failed (${res.status})`);
    }
    return res.json();
  },
  
  getBookings: async ({ statusIn } = {}) => {
    const url = new URL(`${API_URL}/api/bookings`);
    if (statusIn) {
      const value = Array.isArray(statusIn) ? statusIn.join(',') : statusIn;
      if (value) url.searchParams.set('status_in', value);
    }
    const res = await fetch(url.toString(), {
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  cancelBooking: async (bookingId) => {
    const res = await fetch(`${API_URL}/api/bookings/${bookingId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return res.json();
  },

  // -------- Recurring Enrollment Plans (Booking-v2 / Slice B) --------
  // Wraps `/api/enrollment-plans` so a parent can subscribe to a recurring
  // schedule (All Year Round / Term Time Only / Ad-hoc) instead of
  // booking each session individually.
  getEnrollmentPlans: async () => {
    const res = await fetch(`${API_URL}/api/enrollment-plans`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to load plans');
    return res.json();
  },
  createEnrollmentPlan: async (payload) => {
    const res = await fetch(`${API_URL}/api/enrollment-plans`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create plan');
    return res.json();
  },

  // Batch create — same plan replicated across multiple children. Used
  // by the Recurring Plans tab when a family wants the same Mon/Wed/Fri
  // afterschool club for Alice AND Bob. Server creates one independent
  // plan per child so they can later be edited / paused individually.
  createEnrollmentPlansBatch: async (payload) => {
    const res = await fetch(`${API_URL}/api/enrollment-plans/batch`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Batch plan creation failed (${res.status})`);
    }
    return res.json();
  },
  updateEnrollmentPlan: async (planId, payload) => {
    const res = await fetch(`${API_URL}/api/enrollment-plans/${planId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update plan');
    return res.json();
  },
  cancelEnrollmentPlan: async (planId) => {
    const res = await fetch(`${API_URL}/api/enrollment-plans/${planId}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to cancel plan');
    return res.json();
  },
  runEnrollmentPlan: async (planId) => {
    const res = await fetch(`${API_URL}/api/enrollment-plans/${planId}/run`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to run plan');
    return res.json();
  },

  // -------- Meals & Menu Planner --------
  getMeals: async () => {
    const res = await fetch(`${API_URL}/api/meals`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to load meals');
    return res.json();
  },
  createMeal: async (payload) => {
    const res = await fetch(`${API_URL}/api/meals`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create meal');
    return res.json();
  },
  updateMeal: async (mealId, payload) => {
    const res = await fetch(`${API_URL}/api/meals/${mealId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update meal');
    return res.json();
  },
  deleteMeal: async (mealId) => {
    const res = await fetch(`${API_URL}/api/meals/${mealId}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to delete meal');
    return res.json();
  },

  /** Browse the platform-wide catalogue of common UK childcare meals.
   *  Filters: search, category (breakfast/lunch/snack/tea/drink),
   *  slot, allergen_free_of (comma-separated allergen codes). */
  getMealCatalogue: async ({ search, category, slot, allergenFreeOf } = {}) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (category) params.append('category', category);
    if (slot) params.append('slot', slot);
    if (allergenFreeOf?.length) params.append('allergen_free_of', allergenFreeOf.join(','));
    const qs = params.toString();
    const res = await fetch(`${API_URL}/api/meal-catalogue${qs ? `?${qs}` : ''}`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to load catalogue');
    return res.json();
  },

  /** Clone a catalogue entry into the org's editable meal library. */
  cloneMealFromCatalogue: async (body) => {
    const res = await fetch(`${API_URL}/api/meals/from-catalogue`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to clone meal');
    return res.json();
  },

  /** AI-powered allergen suggestion. Returns { allergens, confidence,
   *  safety_note, dietary_tags, source } where source is 'llm' or
   *  'heuristic'. The UI must mark non-LLM results visually. */
  suggestMealAllergens: async ({ name, description }) => {
    const res = await fetch(`${API_URL}/api/meals/suggest-allergens`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, description }),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to get allergen suggestions');
    return res.json();
  },
  getMenuPlans: async () => {
    const res = await fetch(`${API_URL}/api/menu-plans`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to load menu plans');
    return res.json();
  },
  createMenuPlan: async (payload) => {
    const res = await fetch(`${API_URL}/api/menu-plans`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create menu plan');
    return res.json();
  },
  updateMenuPlan: async (planId, payload) => {
    const res = await fetch(`${API_URL}/api/menu-plans/${planId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update menu plan');
    return res.json();
  },
  deleteMenuPlan: async (planId) => {
    const res = await fetch(`${API_URL}/api/menu-plans/${planId}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to delete menu plan');
    return res.json();
  },
  setMenuOverride: async (planId, payload) => {
    const res = await fetch(`${API_URL}/api/menu-plans/${planId}/override`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to save override');
    return res.json();
  },
  clearMenuOverride: async (planId, dateISO, slot) => {
    const res = await fetch(`${API_URL}/api/menu-plans/${planId}/override?date=${encodeURIComponent(dateISO)}&slot=${encodeURIComponent(slot)}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to clear override');
    return res.json();
  },
  getActiveMenu: async (dateISO) => {
    const q = dateISO ? `?date=${encodeURIComponent(dateISO)}` : '';
    const res = await fetch(`${API_URL}/api/menu-plans/active${q}`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to load active menu');
    return res.json();
  },
  getWeekMenu: async (weekStartISO) => {
    const q = weekStartISO ? `?week_start=${encodeURIComponent(weekStartISO)}` : '';
    const res = await fetch(`${API_URL}/api/menu-plans/week${q}`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to load weekly menu');
    return res.json();
  },

  // -------- Web Push (Slice B) --------
  getPushConfig: async () => {
    const res = await fetch(`${API_URL}/api/push/config`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to load push config');
    return res.json();
  },
  pushSubscribe: async (subscription) => {
    const res = await fetch(`${API_URL}/api/push/subscribe`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(subscription),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to subscribe');
    return res.json();
  },
  pushUnsubscribe: async (endpoint) => {
    const res = await fetch(`${API_URL}/api/push/subscribe?endpoint=${encodeURIComponent(endpoint)}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    return res.json();
  },
  pushTest: async () => {
    const res = await fetch(`${API_URL}/api/push/test`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    return res.json();
  },

  // -------- Communications (used by MessageComposer) --------
  // The existing backend `/api/communications` POST is used directly;
  // these helpers just give the frontend a single import surface.
  sendCommunication: async (payload) => {
    const res = await fetch(`${API_URL}/api/communications`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to send message');
    }
    return res.json();
  },

  // -------- Payment Intents (grouped booking payments) --------
  // Bundles multiple pending bookings into a single Mollie checkout so
  // a parent booking 3 kids × 5 sessions is billed once, not 15 times.
  createPaymentIntentForBookings: async (bookingIds) => {
    const res = await fetch(`${API_URL}/api/payment-intents/for-bookings`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ booking_ids: bookingIds }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to create payment intent');
    }
    return res.json();
  },
  getPaymentIntent: async (intentId) => {
    const res = await fetch(`${API_URL}/api/payment-intents/${intentId}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to load payment intent');
    }
    return res.json();
  },
  startPaymentIntentMollie: async (intentId) => {
    const res = await fetch(`${API_URL}/api/payment-intents/${intentId}/pay/mollie`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to start card payment');
    }
    return res.json();
  },
  startPaymentIntentBankTransfer: async (intentId) => {
    const res = await fetch(`${API_URL}/api/payment-intents/${intentId}/pay/bank-transfer`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to start bank transfer');
    }
    return res.json();
  },
  markPaymentIntentPaid: async (intentId, payload) => {
    const res = await fetch(`${API_URL}/api/payment-intents/${intentId}/mark-paid`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to mark intent paid');
    }
    return res.json();
  },
  refundPaymentIntent: async (intentId, payload) => {
    const res = await fetch(`${API_URL}/api/payment-intents/${intentId}/refund`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Refund failed');
    }
    return res.json();
  },
  // Admin-only listing of payment intents in the org.
  // params: { status?, limit?, search? }
  listPaymentIntents: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.search) qs.set('search', params.search);
    const url = `${API_URL}/api/payment-intents${qs.toString() ? `?${qs.toString()}` : ''}`;
    const res = await fetch(url, { headers: getAuthHeader() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to load payment intents');
    }
    return res.json();
  },

  // -------- Public Welcome Page (per-org marketing landing) --------
  // Public fetch — no auth. Returns 404 until the org owner publishes.
  getPublicWelcomePage: async (slug) => {
    const res = await fetch(`${API_URL}/api/public/welcome/${encodeURIComponent(slug)}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Page not found');
    }
    return res.json();
  },
  // Public signup — always lands as pending parent.
  submitWelcomePageSignup: async (slug, payload) => {
    const res = await fetch(`${API_URL}/api/public/welcome/${encodeURIComponent(slug)}/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Signup failed');
    }
    return res.json();
  },
  // Admin: fetch (drafts included) + update the welcome page config.
  getMyWelcomePage: async () => {
    const res = await fetch(`${API_URL}/api/organizations/me/welcome-page`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to load welcome page');
    }
    return res.json();
  },
  updateMyWelcomePage: async (patch) => {
    const res = await fetch(`${API_URL}/api/organizations/me/welcome-page`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(patch || {}),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to update welcome page');
    }
    return res.json();
  },
  
  // Waitlist
  getWaitlist: async () => {
    const res = await fetch(`${API_URL}/api/waitlist`, {
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  claimWaitlistSpot: async (claimData) => {
    const res = await fetch(`${API_URL}/api/waitlist/claim`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(claimData)
    });
    return res.json();
  },
  
  // Notifications
  getNotifications: async () => {
    const res = await fetch(`${API_URL}/api/notifications`, {
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  markNotificationRead: async (notificationId) => {
    const res = await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
      method: 'PUT',
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  // Ledger
  getLedger: async () => {
    const res = await fetch(`${API_URL}/api/ledger`, {
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  getBalance: async () => {
    const res = await fetch(`${API_URL}/api/ledger/balance`, {
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  // Moments
  getMoments: async (childId = null) => {
    const params = childId ? `?child_id=${childId}` : '';
    const res = await fetch(`${API_URL}/api/moments${params}`, {
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  createMoment: async (momentData) => {
    const res = await fetch(`${API_URL}/api/moments`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(momentData)
    });
    return res.json();
  },
  
  // Dashboard
  getDashboardStats: async () => {
    const res = await fetch(`${API_URL}/api/dashboard/stats`, {
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  // Activities
  getActivities: async () => {
    const res = await fetch(`${API_URL}/api/activities`, {
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  createActivity: async (activityData) => {
    const res = await fetch(`${API_URL}/api/activities`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(activityData)
    });
    return res.json();
  },
  
  // Users (Admin)
  getUsers: async () => {
    const res = await fetch(`${API_URL}/api/users`, {
      headers: getAuthHeader()
    });
    return res.json();
  },

  // Directory — minimal user list any tenant user can call
  // (used by MessageComposer to populate parent + staff pickers).
  getDirectoryUsers: async () => {
    const res = await fetch(`${API_URL}/api/directory/users`, {
      headers: getAuthHeader()
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  },
  
  updateUser: async (userId, updates) => {
    const res = await fetch(`${API_URL}/api/users/${userId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    return res.json();
  },
  
  deleteUser: async (userId) => {
    const res = await fetch(`${API_URL}/api/users/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return res.json();
  },

  /**
   * Promote / demote a user along the role tier (staff →
   * room_leader → deputy_manager → org_admin) without creating a new
   * account. `assigned_room_id` is required when promoting to
   * room_leader; pass null for any other role.
   */
  changeUserRole: async (userId, body) => {
    const res = await fetch(`${API_URL}/api/users/${userId}/role`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const detail = await res.json().catch(() => ({}));
      throw new Error(detail.detail || 'Failed to change role');
    }
    return res.json();
  },

  // Pending Parents (Admin approval workflow)
  getPendingParents: async () => {
    const res = await fetch(`${API_URL}/api/users/pending-parents`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to load pending parents');
    }
    return res.json();
  },

  approveUser: async (userId) => {
    const res = await fetch(`${API_URL}/api/users/${userId}/approve`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to approve user');
    }
    return res.json();
  },

  rejectUser: async (userId, reason = '') => {
    const res = await fetch(`${API_URL}/api/users/${userId}/reject`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to reject user');
    }
    return res.json();
  },

  // ---- Timesheets ----
  getTimesheets: async ({ start_date, end_date, staff_id } = {}) => {
    const qs = new URLSearchParams();
    if (start_date) qs.set('start_date', start_date);
    if (end_date) qs.set('end_date', end_date);
    if (staff_id) qs.set('staff_id', staff_id);
    const res = await fetch(`${API_URL}/api/timesheets?${qs.toString()}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to load timesheets');
    }
    return res.json();
  },

  getMyTimesheet: async ({ start_date, end_date } = {}) => {
    const qs = new URLSearchParams();
    if (start_date) qs.set('start_date', start_date);
    if (end_date) qs.set('end_date', end_date);
    const res = await fetch(`${API_URL}/api/timesheets/me?${qs.toString()}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to load my timesheet');
    }
    return res.json();
  },

  /** Triggers a CSV download in the browser. */
  downloadTimesheetsCsv: async ({ start_date, end_date, staff_id, include_volunteers } = {}) => {
    const qs = new URLSearchParams();
    if (start_date) qs.set('start_date', start_date);
    if (end_date) qs.set('end_date', end_date);
    if (staff_id) qs.set('staff_id', staff_id);
    if (include_volunteers) qs.set('include_volunteers', 'true');
    const res = await fetch(`${API_URL}/api/timesheets/export.csv?${qs.toString()}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to export CSV');
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename="?([^"]+)"?/);
    a.download = m ? m[1] : `timesheets_${start_date || ''}_${end_date || ''}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  // ---- Account cleanup (Org Settings → Members) ----
  /** Bulk-reprice bookings. Dry-run returns the preview; pass dry_run=false to commit. */
  repriceBookings: async ({ strategy, dry_run = true, status_in = ['confirmed'], future_only = true, template_ids = [] }) => {
    const res = await fetch(`${API_URL}/api/bookings/reprice`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ strategy, dry_run, status_in, future_only, template_ids }),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Reprice failed');
    return res.json();
  },
  /** Undo the most recent reprice batch for the caller's org. */
  undoLastReprice: async () => {
    const res = await fetch(`${API_URL}/api/bookings/reprice/undo-last`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Undo failed');
    return res.json();
  },
  /** One-shot repair: set is_active=True on staff profiles where the
   *  field is missing/null (legacy data). Idempotent + tenant-scoped. */
  repairStaffProfileActiveStatus: async (mode = 'missing') => {
    const res = await fetch(`${API_URL}/api/staff/profiles/repair-active-status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ mode }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to repair');
    }
    return res.json();
  },

  /** Restore a single archived staff profile (Active again). */
  restoreStaffProfile: async (staffId) => {
    const res = await fetch(`${API_URL}/api/staff/profiles/${staffId}/restore`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to restore staff profile');
    }
    return res.json();
  },

  /** Self-serve onboarding checklist for the calling staff member.
   *  Returns { items:[{key,label,icon,required,tab,hint,done}], total, completed, percent, all_done }. */
  getMyOnboardingChecklist: async () => {
    const res = await fetch(`${API_URL}/api/staff/onboarding-checklist`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load onboarding checklist');
    }
    return res.json();
  },

  /** Trial state for the current user's org. Feeds the countdown banner
   *  at the top of AdminDashboard and the reminder scheduler.
   *  Returns { on_trial, is_expired, plan, billing_interval, trial_ends_at, days_left, hours_left, started_at }. */
  getMyTrialStatus: async () => {
    const res = await fetch(`${API_URL}/api/me/trial-status`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load trial status');
    }
    return res.json();
  },

  // ===========================================================================
  // Payroll Center
  // ===========================================================================

  /** Per-staff payroll snapshot for a period (hours, projected gross,
   * last payslip, payment-details readiness). Defaults to current month. */
  getPayrollPeriodSummary: async ({ start_date, end_date, include_archived, include_volunteers } = {}) => {
    const url = new URL(`${API_URL}/api/payroll/period-summary`);
    if (start_date) url.searchParams.set('start_date', start_date);
    if (end_date) url.searchParams.set('end_date', end_date);
    if (include_archived) url.searchParams.set('include_archived', 'true');
    if (include_volunteers) url.searchParams.set('include_volunteers', 'true');
    const res = await fetch(url.toString(), { headers: getAuthHeader() });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load payroll summary');
    }
    return res.json();
  },

  /** Bulk-generate draft payslips for the period. */
  bulkGeneratePayslips: async ({
    start_date, end_date, staff_ids, include_zero_hours, skip_if_exists, pay_date,
  } = {}) => {
    const res = await fetch(`${API_URL}/api/payroll/bulk-generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({
        start_date, end_date,
        ...(staff_ids ? { staff_ids } : {}),
        include_zero_hours: !!include_zero_hours,
        skip_if_exists: skip_if_exists !== false,
        ...(pay_date ? { pay_date } : {}),
      }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Bulk generate failed');
    }
    return res.json();
  },

  // ===========================================================================
  // Staff → Children (unified read-only child profile for floor staff)
  // ===========================================================================

  /** List children visible to the calling staff member (center-scoped). */
  staffListChildren: async (search) => {
    const url = new URL(`${API_URL}/api/staff/children`);
    if (search) url.searchParams.set('search', search);
    const res = await fetch(url.toString(), { headers: getAuthHeader() });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load children');
    }
    return res.json();
  },

  /** Aggregated child profile: basics, safety, parent, collectors,
   * attendance window, moments, reports, milestones, incidents. */
  staffGetChildProfile: async (childId, { attendance_days } = {}) => {
    const url = new URL(`${API_URL}/api/staff/children/${childId}/profile`);
    if (attendance_days) url.searchParams.set('attendance_days', String(attendance_days));
    const res = await fetch(url.toString(), { headers: getAuthHeader() });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load child profile');
    }
    return res.json();
  },

  // ===========================================================================
  // Events (trips / in-house / visitor sessions / parent sessions / celebrations)
  // ===========================================================================

  /** List all users in the caller's organization (admin only). Used by
   *  EventDrawer to populate the staff allocation picker. */
  getAllUsers: async () => {
    const res = await fetch(`${API_URL}/api/users`, { headers: getAuthHeader() });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load users');
    }
    return res.json();
  },

  listEvents: async ({ date_from, date_to, status, event_type } = {}) => {
    const url = new URL(`${API_URL}/api/events`);
    if (date_from) url.searchParams.set('date_from', date_from);
    if (date_to) url.searchParams.set('date_to', date_to);
    if (status) url.searchParams.set('status', status);
    if (event_type) url.searchParams.set('event_type', event_type);
    const res = await fetch(url.toString(), { headers: getAuthHeader() });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load events');
    }
    return res.json();
  },

  getEvent: async (eventId) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}`, { headers: getAuthHeader() });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load event');
    }
    return res.json();
  },

  createEvent: async (payload) => {
    // organization_id is required by Pydantic but the backend overrides it
    // from the JWT — send a placeholder.
    const body = { organization_id: 'placeholder', ...payload };
    const res = await fetch(`${API_URL}/api/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to create event');
    }
    return res.json();
  },

  updateEvent: async (eventId, payload) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update event');
    }
    return res.json();
  },

  deleteEvent: async (eventId) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete event');
    }
    return res.json();
  },

  addEventAttendees: async (eventId, child_ids, opts = {}) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/attendees`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({
        child_ids,
        override_consent: !!opts.override_consent,
        ...(opts.override_reason ? { override_reason: opts.override_reason } : {}),
      }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to add attendees');
    }
    return res.json();
  },

  removeEventAttendee: async (eventId, childId) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/attendees/${childId}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to remove attendee');
    }
    return res.json();
  },

  getEventConsentCheck: async (eventId) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/consent-check`, { headers: getAuthHeader() });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load consent check');
    }
    return res.json();
  },

  /** Upload a single photo / clip file to GridFS. Returns { media_id }. */
  uploadMomentMedia: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_URL}/api/moments/upload-media`, {
      method: 'POST',
      headers: getAuthHeader(),  // do NOT set Content-Type for multipart
      body: fd,
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to upload media');
    }
    return res.json();
  },

  /** Post media to an event — fans out one Moment per (attendee × media). */
  createEventMoments: async (eventId, { media_ids, caption, child_ids, tags } = {}) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/moments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({
        media_ids,
        ...(caption ? { caption } : {}),
        ...(child_ids ? { child_ids } : {}),
        ...(tags ? { tags } : {}),
      }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to post event moment');
    }
    return res.json();
  },

  /** List moments attached to an event, grouped by upload batch. */
  listEventMoments: async (eventId) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/moments`, { headers: getAuthHeader() });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load event moments');
    }
    return res.json();
  },

  // ===========================================================================
  // Events — Iteration B (notifications, register, headcount, RA)
  // ===========================================================================

  /** Fire branded parent emails for this event.
   *  kind ∈ "scheduled" | "reminder" | "cancelled" | "custom"
   */
  notifyEventParents: async (eventId, { kind = 'scheduled', message } = {}) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/notify-parents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ kind, ...(message ? { message } : {}) }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to send event notifications');
    }
    return res.json();
  },

  getEventAttendance: async (eventId) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/attendance`, { headers: getAuthHeader() });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load attendance');
    }
    return res.json();
  },

  /** Bulk upsert attendance entries.
   *  updates: [{child_id, status, note?}]
   */
  bulkUpsertEventAttendance: async (eventId, updates) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/attendance/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ updates }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update attendance');
    }
    return res.json();
  },

  listEventHeadcounts: async (eventId) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/headcounts`, { headers: getAuthHeader() });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load headcounts');
    }
    return res.json();
  },

  createEventHeadcount: async (eventId, { count, expected, location, note } = {}) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/headcounts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({
        count,
        ...(expected != null ? { expected } : {}),
        ...(location ? { location } : {}),
        ...(note ? { note } : {}),
      }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to record headcount');
    }
    return res.json();
  },

  /** Upload a document (PDF/DOC/IMG) and attach it to an event as its RA.
   *  Two-step: upload to /api/files/upload?category=documents, then POST the
   *  returned file_id to /api/events/{id}/risk-assessment.
   */
  uploadAndAttachEventRiskAssessment: async (eventId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const uploadUrl = new URL(`${API_URL}/api/files/upload`);
    uploadUrl.searchParams.set('category', 'documents');
    uploadUrl.searchParams.set('linked_type', 'event');
    uploadUrl.searchParams.set('linked_id', eventId);
    const uploadRes = await fetch(uploadUrl.toString(), {
      method: 'POST',
      headers: getAuthHeader(),  // do NOT set Content-Type for FormData
      body: fd,
    });
    if (!uploadRes.ok) {
      const e = await uploadRes.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to upload risk-assessment file');
    }
    const uploaded = await uploadRes.json();

    const attachRes = await fetch(`${API_URL}/api/events/${eventId}/risk-assessment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ file_id: uploaded.file_id }),
    });
    if (!attachRes.ok) {
      const e = await attachRes.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to attach risk-assessment file');
    }
    return attachRes.json();
  },

  getEventRiskAssessment: async (eventId) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/risk-assessment`, { headers: getAuthHeader() });
    if (res.status === 404) return null;
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load risk assessment');
    }
    return res.json();
  },

  clearEventRiskAssessment: async (eventId) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/risk-assessment`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to clear risk assessment');
    }
    return res.json();
  },

  // ===========================================================================
  // Events — Recap video (Iteration C)
  // ===========================================================================

  /** Kick off a recap render. The server returns immediately with
   *  `status="queued"`; poll `getEventRecap` until ready / failed.
   */
  generateEventRecap: async (eventId, { mediaIds, makePublic = true, shareTtlDays = 90, title, subtitle, withMusic, musicMediaId, musicVolume } = {}) => {
    const body = {
      ...(Array.isArray(mediaIds) ? { media_ids: mediaIds } : {}),
      make_public: makePublic,
      share_ttl_days: shareTtlDays,
      ...(title ? { title } : {}),
      ...(subtitle ? { subtitle } : {}),
      ...(withMusic !== undefined ? { with_music: !!withMusic } : {}),
      ...(musicMediaId ? { music_media_id: musicMediaId } : {}),
      ...(musicVolume !== undefined ? { music_volume: Number(musicVolume) } : {}),
    };
    const res = await fetch(`${API_URL}/api/events/${eventId}/recap/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to start recap render');
    }
    return res.json();
  },

  // ----- Recap music library (org-scoped) -----

  /** List background-music tracks an admin has uploaded for this org. */
  listRecapMusic: async () => {
    const res = await fetch(`${API_URL}/api/recap-music`, { headers: getAuthHeader() });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load music library');
    }
    return res.json();
  },

  /** Upload an MP3/M4A/WAV — admin-only. Returns the new track id + meta. */
  uploadRecapMusic: async (file, title) => {
    const fd = new FormData();
    fd.append('file', file);
    if (title) fd.append('title', title);
    const res = await fetch(`${API_URL}/api/recap-music/upload`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: fd,
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to upload track');
    }
    return res.json();
  },

  /** Soft-delete a music track from the library. */
  deleteRecapMusic: async (trackId) => {
    const res = await fetch(`${API_URL}/api/recap-music/${trackId}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete track');
    }
    return res.json();
  },

  /** Returns the most-recent recap doc for an event, or null on 404. */
  getEventRecap: async (eventId) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/recap`, { headers: getAuthHeader() });
    if (res.status === 404) return null;
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load recap');
    }
    return res.json();
  },

  /** Toggle public share, rotate token, or extend expiry. */
  updateEventRecapShare: async (eventId, { isPublic, rotateToken, shareTtlDays } = {}) => {
    const body = {
      ...(isPublic !== undefined ? { is_public: isPublic } : {}),
      ...(rotateToken ? { rotate_token: true } : {}),
      ...(shareTtlDays !== undefined ? { share_ttl_days: shareTtlDays } : {}),
    };
    const res = await fetch(`${API_URL}/api/events/${eventId}/recap/share`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update share settings');
    }
    return res.json();
  },

  deleteEventRecap: async (eventId) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/recap`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete recap');
    }
    return res.json();
  },

  /** URL of the **streaming endpoint** for embedding in <video src=…>. */
  buildRecapStreamUrl: (token) => `${API_URL}/api/share/event-recap/${token}`,

  /** URL of the **branded landing page** safe to paste in emails/SMS. */
  buildRecapShareUrl: (token) => {
    // In the browser, derive the human-visible origin (e.g. kidswiseapp.com)
    // so the link parents see matches their org's domain. Falls back to
    // the API host when running outside a browser (e.g. SSR/tests).
    try {
      if (typeof window !== 'undefined' && window.location && window.location.origin) {
        return `${window.location.origin}/share/event-recap/${token}`;
      }
    } catch { /* fall through */ }
    return `${API_URL}/share/event-recap/${token}`;
  },

  // ===========================================================================
  // Billing — Mollie subscriptions + plan-limit usage
  // ===========================================================================

  /** Start a Mollie Checkout session; caller redirects to `url`. */
  startBillingCheckout: async ({ planKey, interval = 'monthly' } = {}) => {
    const res = await fetch(`${API_URL}/api/billing/checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ plan_key: planKey, interval }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(typeof e.detail === 'string' ? e.detail : 'Failed to start checkout');
    }
    return res.json();
  },

  /** Poll a Checkout session's status — used by /billing/success. */
  getBillingCheckoutStatus: async (sessionId) => {
    const res = await fetch(`${API_URL}/api/billing/checkout-status/${sessionId}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load checkout status');
    }
    return res.json();
  },

  /** Open the in-app billing management view (Mollie doesn't have a hosted portal). */
  openBillingPortal: async () => {
    const res = await fetch(`${API_URL}/api/billing/portal-session`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to open billing portal');
    }
    return res.json();
  },

  /** Combined plan + status + usage payload for the admin Billing tab. */
  getBillingSubscription: async () => {
    const res = await fetch(`${API_URL}/api/billing/subscription`, { headers: getAuthHeader() });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load billing state');
    }
    return res.json();
  },

  /** Per-limit usage + warn/exceeded flags for the dashboard banner. */
  getBillingUsage: async () => {
    const res = await fetch(`${API_URL}/api/billing/usage`, { headers: getAuthHeader() });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load usage');
    }
    return res.json();
  },

  /** Pre-flight summary: counts attached records + flags clean/archive eligibility. */
  getAccountImpact: async (userId) => {
    const res = await fetch(`${API_URL}/api/users/${userId}/account-impact`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to load account impact');
    return res.json();
  },
  /** Delete or archive a user account. mode = 'clean' | 'archive'. confirm_email must
   *  match the target's CURRENT email exactly (case-insensitive). */
  deleteAccount: async (userId, { mode, confirm_email }) => {
    const res = await fetch(`${API_URL}/api/users/${userId}/delete-account`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode, confirm_email }),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to delete account');
    return res.json();
  },

  // ---- Transport Manifests (admin view) ----
  /** Get manifests for a specific driver on a date (admin / deputy only).
   *  Drivers/staff cannot peek into other drivers' routes — backend enforces this. */
  getDriverManifests: async ({ driver_id, date } = {}) => {
    const qs = new URLSearchParams();
    if (driver_id) qs.set('driver_id', driver_id);
    if (date) qs.set('date', date);
    const res = await fetch(`${API_URL}/api/manifests?${qs.toString()}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed to load manifests');
    return res.json();
  },

  // ---- Staff Availability ----
  getAvailability: async (staffId) => {
    const qs = staffId ? `?staff_id=${encodeURIComponent(staffId)}` : '';
    const res = await fetch(`${API_URL}/api/staff/availability${qs}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to load availability');
    }
    return res.json();
  },

  createAvailability: async (data) => {
    const res = await fetch(`${API_URL}/api/staff/availability`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to create availability');
    }
    return res.json();
  },

  updateAvailability: async (id, updates) => {
    const res = await fetch(`${API_URL}/api/staff/availability/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to update availability');
    }
    return res.json();
  },

  deleteAvailability: async (id) => {
    const res = await fetch(`${API_URL}/api/staff/availability/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to delete availability');
    }
    return res.json();
  },

  // ---- Leave Requests ----
  getLeaveRequests: async ({ status, staff_id } = {}) => {
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    if (staff_id) qs.set('staff_id', staff_id);
    const res = await fetch(`${API_URL}/api/leave-requests?${qs.toString()}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to load leave');
    return res.json();
  },

  createLeaveRequest: async (data) => {
    const res = await fetch(`${API_URL}/api/leave-requests`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to create leave');
    return res.json();
  },

  approveLeave: async (id, note = '') => {
    const res = await fetch(`${API_URL}/api/leave-requests/${id}/approve`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to approve');
    return res.json();
  },

  rejectLeave: async (id, note = '') => {
    const res = await fetch(`${API_URL}/api/leave-requests/${id}/reject`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to reject');
    return res.json();
  },

  cancelLeave: async (id) => {
    const res = await fetch(`${API_URL}/api/leave-requests/${id}`, {
      method: 'DELETE', headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to cancel');
    return res.json();
  },

  /** Annual leave balance — staff omit staffId, admin pass it. */
  getLeaveBalance: async (staffId = null) => {
    const qs = staffId ? `?staff_id=${encodeURIComponent(staffId)}` : '';
    const res = await fetch(`${API_URL}/api/leave-requests/balance${qs}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to load balance');
    return res.json();
  },

  /** Manual trigger for the weekly auto-submit job (admin only). */
  triggerTimesheetAutoSubmit: async () => {
    const res = await fetch(`${API_URL}/api/timesheets/auto-submit-now`, {
      method: 'POST', headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to trigger');
    return res.json();
  },

  // ---- Timesheet approvals ----
  submitTimesheet: async ({ period_start, period_end }) => {
    const res = await fetch(`${API_URL}/api/timesheets/submit`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ period_start, period_end }),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to submit timesheet');
    return res.json();
  },

  approveTimesheet: async (id, note = '') => {
    const res = await fetch(`${API_URL}/api/timesheets/${id}/approve`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to approve timesheet');
    return res.json();
  },

  rejectTimesheet: async (id, note = '') => {
    const res = await fetch(`${API_URL}/api/timesheets/${id}/reject`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to reject timesheet');
    return res.json();
  },

  // Clock-event edit / soft-delete / admin-create. Wired into
  // TimesheetView so admins (and staff for their own unapproved week)
  // can correct missing or wrong punches.
  updateClockEvent: async (eventId, payload) => {
    const res = await fetch(`${API_URL}/api/clock-events/${eventId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to update clock event');
    return res.json();
  },
  deleteClockEvent: async (eventId) => {
    const res = await fetch(`${API_URL}/api/clock-events/${eventId}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to delete clock event');
    return res.json();
  },
  restoreClockEvent: async (eventId) => {
    const res = await fetch(`${API_URL}/api/clock-events/${eventId}/restore`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to restore clock event');
    return res.json();
  },
  adminCreateClockEvent: async (payload) => {
    const res = await fetch(`${API_URL}/api/clock-events/admin`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to add clock event');
    return res.json();
  },
  deleteTimesheetApproval: async (approvalId) => {
    const res = await fetch(`${API_URL}/api/timesheets/${approvalId}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to undo timesheet');
    return res.json();
  },

  // ---- Shift Requests ----
  getShiftRequests: async ({ status } = {}) => {
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    const res = await fetch(`${API_URL}/api/shift-requests?${qs.toString()}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to load shift requests');
    return res.json();
  },

  createShiftRequest: async (data) => {
    const res = await fetch(`${API_URL}/api/shift-requests`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to create request');
    return res.json();
  },

  respondShiftRequest: async (id, accept) => {
    const res = await fetch(`${API_URL}/api/shift-requests/${id}/respond`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ accept }),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to respond');
    return res.json();
  },

  approveShiftRequest: async (id, note = '') => {
    const res = await fetch(`${API_URL}/api/shift-requests/${id}/approve`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to approve');
    return res.json();
  },

  rejectShiftRequest: async (id, note = '') => {
    const res = await fetch(`${API_URL}/api/shift-requests/${id}/reject`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to reject');
    return res.json();
  },

  cancelShiftRequest: async (id) => {
    const res = await fetch(`${API_URL}/api/shift-requests/${id}`, {
      method: 'DELETE', headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to cancel');
    return res.json();
  },
  
  // Holiday Club Days
  getHolidayClubDays: async () => {
    const res = await fetch(`${API_URL}/api/holiday-club-days`, {
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  createHolidayClubDay: async (dayData) => {
    const res = await fetch(`${API_URL}/api/holiday-club-days`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(dayData)
    });
    return res.json();
  },
  
  // Financial Reports
  getFinancialReport: async (startDate = null, endDate = null) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const res = await fetch(`${API_URL}/api/reports/financial?${params}`, {
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  // Staff Profiles
  getStaffProfiles: async () => {
    const res = await fetch(`${API_URL}/api/staff/profiles`, {
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  createStaffProfile: async (profileData) => {
    const res = await fetch(`${API_URL}/api/staff/profiles`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(profileData)
    });
    return res.json();
  },
  
  updateStaffProfile: async (staffId, updates) => {
    const res = await fetch(`${API_URL}/api/staff/profiles/${staffId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    return res.json();
  },
  
  // Rooms
  getRooms: async () => {
    const res = await fetch(`${API_URL}/api/rooms`, {
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  createRoom: async (roomData) => {
    const res = await fetch(`${API_URL}/api/rooms`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(roomData)
    });
    return res.json();
  },
  
  // Shifts
  getShifts: async (startDate = null, endDate = null, roomId = null) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (roomId) params.append('room_id', roomId);
    
    const res = await fetch(`${API_URL}/api/shifts?${params}`, {
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  createShift: async (shiftData) => {
    const res = await fetch(`${API_URL}/api/shifts`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(shiftData)
    });
    const json = await res.json();
    if (!res.ok) {
      // Hard-blocks come back as 409 with {message, errors[], warnings[]}.
      const detail = json.detail;
      if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
        throw new Error(JSON.stringify({ errors: detail.errors, warnings: detail.warnings || [] }));
      }
      throw new Error(detail || 'Failed to create shift');
    }
    return json;
  },

  updateShift: async (shiftId, updates) => {
    const res = await fetch(`${API_URL}/api/shifts/${shiftId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    const json = await res.json();
    if (!res.ok) {
      const detail = json.detail;
      if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
        throw new Error(JSON.stringify({ errors: detail.errors, warnings: detail.warnings || [] }));
      }
      throw new Error(detail || 'Failed to update shift');
    }
    return json;
  },
  
  // Clock Events
  createClockEvent: async (eventData) => {
    const res = await fetch(`${API_URL}/api/clock-events`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(eventData)
    });
    return res.json();
  },
  
  getClockEvents: async (staffId = null, startDate = null, endDate = null) => {
    const params = new URLSearchParams();
    if (staffId) params.append('staff_id', staffId);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const res = await fetch(`${API_URL}/api/clock-events?${params}`, {
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  getCurrentlyClockedIn: async () => {
    const res = await fetch(`${API_URL}/api/clock-events/currently-clocked-in`, {
      headers: getAuthHeader()
    });
    return res.json();
  },

  /**
   * Caller's current clock state (bug-fixed: 48h lookback so overtime
   * shifts crossing midnight are reflected correctly).
   * Returns { has_profile, staff_id, clocked_in, last_event,
   *           last_event_at, hours_since, on_break, location }.
   */
  getMyClockStatus: async () => {
    const res = await fetch(`${API_URL}/api/clock-events/my-status`, {
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load clock status');
    }
    return res.json();
  },
  
  // Consumables
  createConsumableLog: async (logData) => {
    const res = await fetch(`${API_URL}/api/consumables`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(logData)
    });
    return res.json();
  },
  
  getConsumableLogs: async (startDate = null, endDate = null) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const res = await fetch(`${API_URL}/api/consumables?${params}`, {
      headers: getAuthHeader()
    });
    return res.json();
  },
  
  // Live Ratios
  getLiveRatios: async () => {
    const res = await fetch(`${API_URL}/api/ratios/live`, {
      headers: getAuthHeader()
    });
    return res.json();
  },

  // Child Development Features
  getMilestones: async (params = {}) => {
    const query = new URLSearchParams(params);
    const res = await fetch(`${API_URL}/api/milestones?${query}`, {
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
    });
    return res.json();
  },

  createMilestone: async (data) => {
    const res = await fetch(`${API_URL}/api/milestones`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },

  updateMilestone: async (id, data) => {
    const res = await fetch(`${API_URL}/api/milestones/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },

  getLearningReports: async (params = {}) => {
    const query = new URLSearchParams(params);
    const res = await fetch(`${API_URL}/api/learning-reports?${query}`, {
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
    });
    return res.json();
  },

  createLearningReport: async (data) => {
    const res = await fetch(`${API_URL}/api/learning-reports`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },

  getImmunizations: async (params = {}) => {
    const query = new URLSearchParams(params);
    const res = await fetch(`${API_URL}/api/immunizations?${query}`, {
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
    });
    return res.json();
  },

  createImmunization: async (data) => {
    const res = await fetch(`${API_URL}/api/immunizations`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },

  updateImmunization: async (id, data) => {
    const res = await fetch(`${API_URL}/api/immunizations/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },

  getIncidents: async (params = {}) => {
    const query = new URLSearchParams(params);
    const res = await fetch(`${API_URL}/api/incidents?${query}`, {
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
    });
    return res.json();
  },

  createIncident: async (data) => {
    const res = await fetch(`${API_URL}/api/incidents`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },

  /** Attach a single photo to an existing incident (max 5 per incident, 8MB each).
   *  Returns { photo_url, file_id, count, remaining_slots }. */
  uploadIncidentPhoto: async (incidentId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_URL}/api/incidents/${encodeURIComponent(incidentId)}/photos`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to upload photo' }));
      throw new Error(err.detail || 'Failed to upload photo');
    }
    return res.json();
  },

  /** Remove a previously attached incident photo. */
  deleteIncidentPhoto: async (incidentId, fileId) => {
    const res = await fetch(`${API_URL}/api/incidents/${encodeURIComponent(incidentId)}/photos/${encodeURIComponent(fileId)}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to remove photo' }));
      throw new Error(err.detail || 'Failed to remove photo');
    }
    return res.json();
  },

  acknowledgeIncident: async (id, signature) => {
    const res = await fetch(`${API_URL}/api/incidents/${id}/acknowledge`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ signature })
    });
    return res.json();
  },

  getCommunications: async (params = {}) => {
    const query = new URLSearchParams(params);
    const res = await fetch(`${API_URL}/api/communications?${query}`, {
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
    });
    return res.json();
  },

  createCommunication: async (data) => {
    const res = await fetch(`${API_URL}/api/communications`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },

  replyCommunication: async (id, message) => {
    const res = await fetch(`${API_URL}/api/communications/${id}/reply`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ message })
    });
    return res.json();
  },

  markCommunicationRead: async (id) => {
    const res = await fetch(`${API_URL}/api/communications/${id}/read`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
    });
    return res.json();
  },

  getChild: async (childId) => {
    const res = await fetch(`${API_URL}/api/children/${childId}`, {
      headers: getAuthHeader()
    });
    return res.json();
  },

  // Enrollment APIs
  createEnrollment: async (data) => {
    const res = await fetch(`${API_URL}/api/enrollments`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      const detail = typeof e.detail === 'string'
        ? e.detail
        : Array.isArray(e.detail)
          ? e.detail.map((d) => `${(d.loc || []).slice(1).join('.')}: ${d.msg}`).join('; ')
          : 'Failed to create enrollment.';
      const err = new Error(detail);
      err.status = res.status;
      err.fieldErrors = Array.isArray(e.detail) ? e.detail : null;
      throw err;
    }
    return res.json();
  },

  getEnrollments: async (status = null) => {
    const query = status ? `?status=${status}` : '';
    const res = await fetch(`${API_URL}/api/enrollments${query}`, {
      headers: getAuthHeader()
    });
    return res.json();
  },

  getEnrollment: async (enrollmentId) => {
    const res = await fetch(`${API_URL}/api/enrollments/${enrollmentId}`, {
      headers: getAuthHeader()
    });
    return res.json();
  },

  updateEnrollment: async (enrollmentId, data) => {
    const res = await fetch(`${API_URL}/api/enrollments/${enrollmentId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      const detail = typeof e.detail === 'string' ? e.detail : 'Failed to update enrollment.';
      const err = new Error(detail);
      err.status = res.status;
      throw err;
    }
    return res.json();
  },

  submitEnrollment: async (enrollmentId) => {
    const res = await fetch(`${API_URL}/api/enrollments/${enrollmentId}/submit`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      const detail = typeof e.detail === 'string' ? e.detail : 'Failed to submit enrollment.';
      const err = new Error(detail);
      err.status = res.status;
      throw err;
    }
    return res.json();
  },

  reviewEnrollment: async (enrollmentId, reviewData) => {
    const res = await fetch(`${API_URL}/api/enrollments/${enrollmentId}/review`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(reviewData)
    });
    return res.json();
  },

  addEnrollmentDocument: async (enrollmentId, document) => {
    const res = await fetch(`${API_URL}/api/enrollments/${enrollmentId}/documents`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(document)
    });
    return res.json();
  },

  // School Calendar APIs
  getSchoolCalendar: async (academicYear = null) => {
    const query = academicYear ? `?academic_year=${academicYear}` : '';
    const res = await fetch(`${API_URL}/api/school-calendar${query}`, {
      headers: getAuthHeader()
    });
    return res.json();
  },

  createSchoolCalendar: async (calendar) => {
    const res = await fetch(`${API_URL}/api/school-calendar`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(calendar)
    });
    return res.json();
  },

  checkDateType: async (date) => {
    const res = await fetch(`${API_URL}/api/school-calendar/check-date?date=${date}`, {
      headers: getAuthHeader()
    });
    return res.json();
  },

  // Financial Reports
  getPNLReport: async (startDate = null, endDate = null) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const res = await fetch(`${API_URL}/api/reports/pnl?${params}`, {
      headers: getAuthHeader()
    });
    return res.json();
  },

  getAttendanceTrends: async (startDate = null, endDate = null) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const res = await fetch(`${API_URL}/api/reports/attendance-trends?${params}`, {
      headers: getAuthHeader()
    });
    return res.json();
  },

  getLateTrends: async (startDate = null, endDate = null) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const res = await fetch(`${API_URL}/api/reports/late-trends?${params}`, {
      headers: getAuthHeader()
    });
    return res.json();
  },

  // ============================================================================
  // CRUD EXTENSIONS — Edit / Delete endpoints (added in CRUD sweep)
  // ============================================================================

  // ---- Centers ----
  updateOrganizationCenter: async (centerId, updates) => {
    const res = await fetch(`${API_URL}/api/organizations/centers/${centerId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update center');
    }
    return res.json();
  },

  deleteOrganizationCenter: async (centerId, force = false) => {
    const qs = force ? '?force=true' : '';
    const res = await fetch(`${API_URL}/api/organizations/centers/${centerId}${qs}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete center');
    }
    return res.json();
  },

  // ---- Session Templates ----
  updateSessionTemplate: async (templateId, updates) => {
    const res = await fetch(`${API_URL}/api/sessions/templates/${templateId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update template');
    }
    return res.json();
  },

  deleteSessionTemplate: async (templateId, force = false) => {
    const qs = force ? '?force=true' : '';
    const res = await fetch(`${API_URL}/api/sessions/templates/${templateId}${qs}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete template');
    }
    return res.json();
  },

  // ---- Session Instances ----
  updateSessionInstance: async (instanceId, updates) => {
    const res = await fetch(`${API_URL}/api/sessions/instances/${instanceId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update session');
    }
    return res.json();
  },

  cancelSessionInstance: async (instanceId) => {
    const res = await fetch(`${API_URL}/api/sessions/instances/${instanceId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to cancel session');
    }
    return res.json();
  },

  // ---- Children ----
  updateChild: async (childId, updates) => {
    const res = await fetch(`${API_URL}/api/children/${childId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update child');
    }
    return res.json();
  },

  /** Admin/manager only: change a child's payment_plan and/or billing_terms. */
  updateChildPaymentPlan: async (childId, { payment_plan, billing_terms, notes } = {}) => {
    const body = {};
    if (payment_plan !== undefined) body.payment_plan = payment_plan;
    if (billing_terms !== undefined) body.billing_terms = billing_terms;
    if (notes !== undefined) body.notes = notes;
    const res = await fetch(`${API_URL}/api/children/${childId}/payment-plan`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update payment plan');
    }
    return res.json();
  },

  deleteChild: async (childId, force = false) => {
    const qs = force ? '?force=true' : '';
    const res = await fetch(`${API_URL}/api/children/${childId}${qs}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete child');
    }
    return res.json();
  },

  // Pre-flight dependency check for a child. Returns
  // { child_id, child_name, counts, total, has_active_bookings }.
  // Used by the admin "Force delete" dialog to show exactly what
  // records will be wiped before confirming.
  getChildDependencies: async (childId) => {
    const res = await fetch(`${API_URL}/api/children/${childId}/dependencies`, {
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load child dependencies');
    }
    return res.json();
  },

  // ---- Activities ----
  updateActivity: async (activityId, updates) => {
    const res = await fetch(`${API_URL}/api/activities/${activityId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update activity');
    }
    return res.json();
  },

  deleteActivity: async (activityId) => {
    const res = await fetch(`${API_URL}/api/activities/${activityId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete activity');
    }
    return res.json();
  },

  // ---- Holiday Club Days ----
  updateHolidayClubDay: async (dayId, updates) => {
    const res = await fetch(`${API_URL}/api/holiday-club-days/${dayId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update holiday day');
    }
    return res.json();
  },

  deleteHolidayClubDay: async (dayId) => {
    const res = await fetch(`${API_URL}/api/holiday-club-days/${dayId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete holiday day');
    }
    return res.json();
  },

  // ---- Bookings (PUT) ----
  updateBooking: async (bookingId, updates) => {
    const res = await fetch(`${API_URL}/api/bookings/${bookingId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update booking');
    }
    return res.json();
  },

  // ---- Staff Profiles (DELETE) ----
  deleteStaffProfile: async (staffId, force = false) => {
    const qs = force ? '?force=true' : '';
    const res = await fetch(`${API_URL}/api/staff/profiles/${staffId}${qs}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete staff profile');
    }
    return res.json();
  },

  // ---- Rooms (DELETE) ----
  deleteRoom: async (roomId, force = false) => {
    const qs = force ? '?force=true' : '';
    const res = await fetch(`${API_URL}/api/rooms/${roomId}${qs}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete room');
    }
    return res.json();
  },

  // ---- Shifts (DELETE) ----
  deleteShift: async (shiftId) => {
    const res = await fetch(`${API_URL}/api/shifts/${shiftId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete shift');
    }
    return res.json();
  },

  // ---- Consumables ----
  updateConsumableLog: async (logId, updates) => {
    const res = await fetch(`${API_URL}/api/consumables/${logId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update consumable');
    }
    return res.json();
  },

  deleteConsumableLog: async (logId) => {
    const res = await fetch(`${API_URL}/api/consumables/${logId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete consumable');
    }
    return res.json();
  },

  // ---- Vehicles (DELETE) ----
  deleteVehicle: async (vehicleId, force = false) => {
    const qs = force ? '?force=true' : '';
    const res = await fetch(`${API_URL}/api/transit/vehicles/${vehicleId}${qs}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete vehicle');
    }
    return res.json();
  },

  // ---- Routes ----
  updateRoute: async (routeId, updates) => {
    const res = await fetch(`${API_URL}/api/transit/routes/${routeId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update route');
    }
    return res.json();
  },

  deleteRoute: async (routeId) => {
    const res = await fetch(`${API_URL}/api/transit/routes/${routeId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete route');
    }
    return res.json();
  },

  // ---- School Calendar ----
  updateSchoolCalendar: async (calendarId, updates) => {
    const res = await fetch(`${API_URL}/api/school-calendar/${calendarId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update calendar');
    }
    return res.json();
  },

  deleteSchoolCalendar: async (calendarId) => {
    const res = await fetch(`${API_URL}/api/school-calendar/${calendarId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete calendar');
    }
    return res.json();
  },

  // ---- Milestones ----
  deleteMilestone: async (milestoneId) => {
    const res = await fetch(`${API_URL}/api/milestones/${milestoneId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete milestone');
    }
    return res.json();
  },

  // ---- Immunizations ----
  deleteImmunization: async (immunizationId) => {
    const res = await fetch(`${API_URL}/api/immunizations/${immunizationId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete immunization');
    }
    return res.json();
  },

  // ---- Learning Reports ----
  updateLearningReport: async (reportId, updates) => {
    const res = await fetch(`${API_URL}/api/learning-reports/${reportId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update report');
    }
    return res.json();
  },

  deleteLearningReport: async (reportId, reason = null) => {
    const qs = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    const res = await fetch(`${API_URL}/api/learning-reports/${reportId}${qs}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete report');
    }
    return res.json();
  },

  // ---- Incidents ----
  updateIncident: async (incidentId, updates) => {
    const res = await fetch(`${API_URL}/api/incidents/${incidentId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update incident');
    }
    return res.json();
  },

  deleteIncident: async (incidentId, reason = null) => {
    const qs = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    const res = await fetch(`${API_URL}/api/incidents/${incidentId}${qs}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete incident');
    }
    return res.json();
  },

  // ---- Communications ----
  updateCommunication: async (commId, updates) => {
    const res = await fetch(`${API_URL}/api/communications/${commId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update message');
    }
    return res.json();
  },

  deleteCommunication: async (commId) => {
    const res = await fetch(`${API_URL}/api/communications/${commId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete message');
    }
    return res.json();
  },

  // ---- Moments ----
  updateMoment: async (momentId, updates) => {
    const res = await fetch(`${API_URL}/api/moments/${momentId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update moment');
    }
    return res.json();
  },

  deleteMoment: async (momentId) => {
    const res = await fetch(`${API_URL}/api/moments/${momentId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete moment');
    }
    return res.json();
  },

  // ---- Moment comments (threaded parent/staff replies) ----
  // Powers the "💬 N replies" badge on moment cards + the reply thread
  // in the detail modal. Both parents and staff can post and edit their
  // own comment; admins/deputy/room leaders can moderate any comment.
  getMomentComments: async (momentId) => {
    const res = await fetch(`${API_URL}/api/moments/${momentId}/comments`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load comments');
    }
    return res.json();
  },

  createMomentComment: async (momentId, body) => {
    const res = await fetch(`${API_URL}/api/moments/${momentId}/comments`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ body }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to post comment');
    }
    return res.json();
  },

  updateMomentComment: async (momentId, commentId, body) => {
    const res = await fetch(`${API_URL}/api/moments/${momentId}/comments/${commentId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ body }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update comment');
    }
    return res.json();
  },

  deleteMomentComment: async (momentId, commentId) => {
    const res = await fetch(`${API_URL}/api/moments/${momentId}/comments/${commentId}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete comment');
    }
    return res.json();
  },

  // ---- Enrollments ----
  deleteEnrollment: async (enrollmentId) => {
    const res = await fetch(`${API_URL}/api/enrollments/${enrollmentId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete enrollment');
    }
    return res.json();
  },

  // ===========================================================================
  // Email notifications (Slice A — one.com SMTP, multi-tenant)
  // ===========================================================================

  /** Get the current user's email category preferences. */
  getMyEmailPreferences: async () => {
    const res = await fetch(`${API_URL}/api/users/me/email-preferences`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load email preferences');
    }
    return res.json();
  },

  /** Update the current user's email category preferences. */
  updateMyEmailPreferences: async (prefs) => {
    const res = await fetch(`${API_URL}/api/users/me/email-preferences`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(prefs || {}),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update email preferences');
    }
    return res.json();
  },

  /** Org-level email sender identity (From-name / Reply-To). */
  getMyOrgEmailSettings: async () => {
    const res = await fetch(`${API_URL}/api/organizations/me/email-settings`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load org email settings');
    }
    return res.json();
  },

  /** Persist org-level email sender identity overrides. */
  updateMyOrgEmailSettings: async ({ email_sender_name, email_reply_to } = {}) => {
    const res = await fetch(`${API_URL}/api/organizations/me/email-settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({
        ...(email_sender_name !== undefined ? { email_sender_name } : {}),
        ...(email_reply_to !== undefined ? { email_reply_to } : {}),
      }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update org email settings');
    }
    return res.json();
  },

  /** Send a branded test email to the caller (or a specified address). */
  sendOrgTestEmail: async (to) => {
    const res = await fetch(`${API_URL}/api/admin/email/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(to ? { to } : {}),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to send test email');
    }
    return res.json();
  },

  /** Recent email send log for the caller's org. */
  getOrgEmailLog: async (limit = 50) => {
    const res = await fetch(`${API_URL}/api/admin/email/log?limit=${limit}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load email log');
    }
    return res.json();
  },

  // ===========================================================================
  // Payment details & Payslips (admin + staff self-serve)
  // ===========================================================================

  /** Admin-only: load payment & HMRC fields for a staff profile. */
  getStaffPaymentDetails: async (staffId) => {
    const res = await fetch(`${API_URL}/api/staff/${staffId}/payment-details`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load payment details');
    }
    return res.json();
  },

  /** Admin-only: patch payment & HMRC fields. */
  updateStaffPaymentDetails: async (staffId, updates) => {
    const res = await fetch(`${API_URL}/api/staff/${staffId}/payment-details`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(updates || {}),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to update payment details');
    }
    return res.json();
  },

  /** Admin-only: generate a payslip. `send_email=true` mails the PDF immediately. */
  createStaffPayslip: async (staffId, payload) => {
    const res = await fetch(`${API_URL}/api/staff/${staffId}/payslips`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload || {}),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to create payslip');
    }
    return res.json();
  },

  /** Admin-only: list a staff member's payslip history. */
  listStaffPayslips: async (staffId, limit = 50) => {
    const res = await fetch(`${API_URL}/api/staff/${staffId}/payslips?limit=${limit}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load payslips');
    }
    return res.json();
  },

  /**
   * Pull a single staff member's aggregated timesheet for a date range.
   * Used by the PayslipDialog "Pull from timesheet" button so admins
   * don't have to retype hours that the clock-events aggregator
   * already computed.
   */
  getStaffTimesheet: async (staffId, startDate, endDate) => {
    const url = new URL(`${API_URL}/api/timesheets`);
    url.searchParams.set('staff_id', staffId);
    if (startDate) url.searchParams.set('start_date', startDate);
    if (endDate) url.searchParams.set('end_date', endDate);
    const res = await fetch(url.toString(), { headers: getAuthHeader() });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load timesheet');
    }
    return res.json();
  },

  /** Self-serve: my own payslip history. */
  listMyPayslips: async () => {
    const res = await fetch(`${API_URL}/api/payslips/me`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load my payslips');
    }
    return res.json();
  },

  /** Return a Blob URL for the PDF so the caller can open / download it. */
  downloadPayslipPdfUrl: async (payslipId) => {
    const res = await fetch(`${API_URL}/api/payslips/${payslipId}/download`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to download payslip');
    }
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },

  /** Re-send (or first-send) a payslip via email. */
  sendPayslipEmail: async (payslipId) => {
    const res = await fetch(`${API_URL}/api/payslips/${payslipId}/send`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to send payslip email');
    }
    return res.json();
  },

  /** Admin-only: flag payslip as paid (book-keeping only — no money moves). */
  markPayslipPaid: async (payslipId) => {
    const res = await fetch(`${API_URL}/api/payslips/${payslipId}/mark-paid`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to mark payslip paid');
    }
    return res.json();
  },

  /** Admin-only: delete a draft payslip. */
  deletePayslip: async (payslipId) => {
    const res = await fetch(`${API_URL}/api/payslips/${payslipId}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete payslip');
    }
    return res.json();
  },

  // ===========================================================================
  // Workspace Invites (Phase C of Workspace link sharing)
  // ===========================================================================

  /** Admin-only: create a new workspace invite (creates record + emails it). */
  createWorkspaceInvite: async ({ email, role, center_id, personal_message } = {}) => {
    const res = await fetch(`${API_URL}/api/workspace-invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({
        email,
        role,
        ...(center_id ? { center_id } : {}),
        ...(personal_message ? { personal_message } : {}),
      }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to create invite');
    }
    return res.json();
  },

  /** Admin-only: list invites in current org (optional status filter). */
  listWorkspaceInvites: async (statusFilter) => {
    const url = new URL(`${API_URL}/api/workspace-invites`);
    if (statusFilter) url.searchParams.set('status_filter', statusFilter);
    const res = await fetch(url.toString(), { headers: getAuthHeader() });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to load invites');
    }
    return res.json();
  },

  /** Admin-only: resend the invite email + extend expiry. */
  resendWorkspaceInvite: async (inviteId) => {
    const res = await fetch(`${API_URL}/api/workspace-invites/${inviteId}/resend`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to resend invite');
    }
    return res.json();
  },

  /** Admin-only: revoke a pending invite. */
  revokeWorkspaceInvite: async (inviteId) => {
    const res = await fetch(`${API_URL}/api/workspace-invites/${inviteId}/revoke`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to revoke invite');
    }
    return res.json();
  },

  /** Admin-only: hard-delete an invite record (revoked/expired cleanup). */
  deleteWorkspaceInvite: async (inviteId) => {
    const res = await fetch(`${API_URL}/api/workspace-invites/${inviteId}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to delete invite');
    }
    return res.json();
  },

  // ----- Public (no auth) — used by the /invite/:token acceptance page -----

  /** Public: look up invite metadata + branding by its token. */
  getInviteByToken: async (token) => {
    const res = await fetch(`${API_URL}/api/workspace-invites/accept/${encodeURIComponent(token)}`);
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Invite not found or no longer valid.');
    }
    return res.json();
  },

  /** Public: accept invite — creates/links account, returns JWT + user. */
  acceptInviteByToken: async (token, { full_name, password, phone } = {}) => {
    const res = await fetch(`${API_URL}/api/workspace-invites/accept/${encodeURIComponent(token)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ full_name, password, phone }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to accept invite');
    }
    return res.json();
  },

  // =========================================================================
  // Phase M — Staff profile gap fixes
  // =========================================================================

  // Archive (leaver) — body: {leaver_reason, leaver_reason_notes,
  // last_working_day, is_rehire_eligible}
  archiveStaffProfile: async (staffId, payload) => {
    const res = await fetch(`${API_URL}/api/staff/profiles/${staffId}/archive`, {
      method: 'PATCH',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Archive failed (${res.status})`);
    }
    return res.json();
  },
  // Restore (rehire) — body: {restart_date?, notes?}
  restoreStaffProfileWithHistory: async (staffId, payload = {}) => {
    const res = await fetch(`${API_URL}/api/staff/profiles/${staffId}/restore`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Restore failed (${res.status})`);
    }
    return res.json();
  },

  // --- Custom competencies (per-org) ---
  listCustomCompetencies: async (includeArchived = false) => {
    const q = includeArchived ? '?include_archived=true' : '';
    const res = await fetch(`${API_URL}/api/custom-competencies${q}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error(`Failed to load competencies (${res.status})`);
    return res.json();
  },
  createCustomCompetency: async (payload) => {
    const res = await fetch(`${API_URL}/api/custom-competencies`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Create failed (${res.status})`);
    }
    return res.json();
  },
  updateCustomCompetency: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/custom-competencies/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Update failed (${res.status})`);
    }
    return res.json();
  },
  deleteCustomCompetency: async (id) => {
    const res = await fetch(`${API_URL}/api/custom-competencies/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error(`Delete failed (${res.status})`);
    return res.json();
  },

  // --- Staff documents (HR: DBS, passport, contract, etc.) ---
  //
  // Multipart POST — pass `file` as a File object plus optional
  // document_type, issue_date, expiry_date, notes. Backend returns the
  // document record with a fresh signed download_url.
  uploadStaffDocument: async (staffId, { file, document_type, issue_date, expiry_date, notes }) => {
    const form = new FormData();
    form.append('file', file);
    if (document_type) form.append('document_type', document_type);
    if (issue_date) form.append('issue_date', issue_date);
    if (expiry_date) form.append('expiry_date', expiry_date);
    if (notes) form.append('notes', notes);
    const res = await fetch(`${API_URL}/api/staff/profiles/${staffId}/documents/upload`, {
      method: 'POST',
      headers: getAuthHeader(), // NB: don't set Content-Type; browser sets multipart boundary
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Upload failed (${res.status})`);
    }
    return res.json();
  },
  listStaffDocuments: async (staffId) => {
    const res = await fetch(`${API_URL}/api/staff/profiles/${staffId}/documents`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error(`Failed to load documents (${res.status})`);
    return res.json();
  },
  deleteStaffDocument: async (staffId, docId) => {
    const res = await fetch(`${API_URL}/api/staff/profiles/${staffId}/documents/${docId}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error(`Delete failed (${res.status})`);
    return res.json();
  },

  // =========================================================================
  // Phase N — Payroll auto-deductions + P60/P45
  // =========================================================================

  // Preview PAYE/NI/pension/student-loan for a proposed payslip WITHOUT
  // saving anything. Returns { deductions[], employer_costs, ytd,
  // ytd_including_this, breakdown }.
  previewAutoPayslip: async (payload) => {
    const res = await fetch(`${API_URL}/api/payroll/payslips/preview-auto`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Preview failed (${res.status})`);
    }
    return res.json();
  },
  createAutoPayslip: async (payload) => {
    const res = await fetch(`${API_URL}/api/payroll/payslips/create-auto`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Auto-create failed (${res.status})`);
    }
    return res.json();
  },
  // Trigger a browser download of a P60 or P45 PDF for the given staff.
  // Uses a temporary <a download> so the file lands in Downloads with
  // a sensible filename from Content-Disposition.
  downloadP60: async (staffId, taxYear) => {
    const res = await fetch(`${API_URL}/api/payroll/p60/${staffId}?tax_year=${encodeURIComponent(taxYear)}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `P60 download failed (${res.status})`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `P60-${taxYear}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
  downloadP45: async (staffId, leaverDate) => {
    const q = leaverDate ? `?leaver_date=${encodeURIComponent(leaverDate)}` : '';
    const res = await fetch(`${API_URL}/api/payroll/p45/${staffId}${q}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `P45 download failed (${res.status})`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `P45-${leaverDate || 'leaver'}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  // Contractor remittance advice (Phase N.7). Body shape:
  //   { period_start, period_end, payment_date, line_items[], vat_rate?, reference?, notes? }
  downloadContractorRemittance: async (staffId, payload) => {
    const res = await fetch(`${API_URL}/api/payroll/remittance/${staffId}`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Remittance failed (${res.status})`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Remittance-${payload.payment_date || 'contractor'}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  // Task 1 — Remittance history list + re-download from S3.
  listContractorRemittances: async (staffId) => {
    const res = await fetch(`${API_URL}/api/payroll/remittance/${staffId}/history`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error(`Failed to load history (${res.status})`);
    return res.json();
  },
  downloadSavedRemittance: async (remittanceId) => {
    const res = await fetch(`${API_URL}/api/payroll/remittance/download/${remittanceId}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Re-download failed (${res.status})`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Remittance-${remittanceId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  // Task 2 — Org-level HMRC/PAYE settings that flow into PDF templates.
  getOrgPayrollSettings: async () => {
    const res = await fetch(`${API_URL}/api/organizations/me/payroll-settings`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error(`Failed to load payroll settings (${res.status})`);
    return res.json();
  },
  updateOrgPayrollSettings: async (payload) => {
    const res = await fetch(`${API_URL}/api/organizations/me/payroll-settings`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Update failed (${res.status})`);
    }
    return res.json();
  },

  // ---- Organization bank details (Parent PAYG payment flow) ----
  getOrgBankDetails: async () => {
    const res = await fetch(`${API_URL}/api/organizations/me/bank-details`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to load bank details');
    return res.json();
  },
  updateOrgBankDetails: async (payload) => {
    const res = await fetch(`${API_URL}/api/organizations/me/bank-details`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Update failed (${res.status})`);
    }
    return res.json();
  },
  getOrgBankDetailsPublic: async () => {
    const res = await fetch(`${API_URL}/api/organizations/me/bank-details/public`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to load bank details');
    return res.json();
  },

  // ---- Booking payments (PAYG parents) ----
  startBookingMolliePayment: async (bookingId) => {
    const res = await fetch(`${API_URL}/api/bookings/${bookingId}/pay/mollie`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Card payment failed (${res.status})`);
    }
    return res.json();
  },
  startBookingBankTransfer: async (bookingId) => {
    const res = await fetch(`${API_URL}/api/bookings/${bookingId}/pay/bank-transfer`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Bank transfer setup failed (${res.status})`);
    }
    return res.json();
  },
  confirmBookingPayment: async (bookingId, note) => {
    const res = await fetch(`${API_URL}/api/bookings/${bookingId}/confirm-payment`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note || null }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Confirmation failed (${res.status})`);
    }
    return res.json();
  },
  listPendingPaymentBookings: async () => {
    const res = await fetch(`${API_URL}/api/bookings/pending-payments`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to load pending payments');
    return res.json();
  },

  // ---- Notification preferences (per-user channel picker) ----
  getNotificationPreferences: async () => {
    const res = await fetch(`${API_URL}/api/me/notification-preferences`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to load notification preferences');
    return res.json();
  },
  updateNotificationPreferences: async (payload) => {
    const res = await fetch(`${API_URL}/api/me/notification-preferences`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Update failed (${res.status})`);
    }
    return res.json();
  },

  // ---- Policies Library ----
  // Parent-facing: only returns published policies.
  listPublishedPolicies: async () => {
    const res = await fetch(`${API_URL}/api/policies`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to load policies');
    return res.json();
  },
  getPublishedPolicy: async (slug) => {
    const res = await fetch(`${API_URL}/api/policies/${slug}`, { headers: getAuthHeader() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Policy not found');
    }
    return res.json();
  },
  // Returns a same-origin URL suitable for opening in a new tab. The
  // token is passed as a querystring param because the printable page
  // opens in a *new* window that doesn't share our fetch auth headers.
  getPolicyPrintableUrl: (slug) => {
    const token = localStorage.getItem('token') || '';
    return `${API_URL}/api/policies/${slug}/printable?token=${encodeURIComponent(token)}`;
  },

  // Admin-facing: includes drafts + missing entries so the librarian UI
  // can offer "Generate" on rows that don't yet exist.
  adminListPolicies: async () => {
    const res = await fetch(`${API_URL}/api/admin/policies`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to load policies');
    return res.json();
  },
  adminUpdatePolicy: async (slug, patch) => {
    const res = await fetch(`${API_URL}/api/admin/policies/${slug}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Update failed (${res.status})`);
    }
    return res.json();
  },
  adminSeedPolicies: async () => {
    const res = await fetch(`${API_URL}/api/admin/policies/seed`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Seed failed (${res.status})`);
    }
    return res.json();
  },
  // Re-renders a single default policy from its registry template
  // using the org's *current* Organisation Settings, wiping any
  // placeholder tokens that were rendered when the values were blank.
  // Only works for default policies (custom policies have no template).
  adminResetPolicy: async (slug) => {
    const res = await fetch(`${API_URL}/api/admin/policies/${encodeURIComponent(slug)}/reset`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Reset failed (${res.status})`);
    }
    return res.json();
  },
  // Bulk-reset every default policy whose body currently contains
  // unresolved placeholder tokens. Called from the "Reset all N"
  // button on the Policies Library placeholder-audit banner.
  adminResetAllPoliciesWithPlaceholders: async () => {
    const res = await fetch(`${API_URL}/api/admin/policies/reset-with-placeholders`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Bulk reset failed (${res.status})`);
    }
    return res.json();
  },

  // ---- Custom (org-specific) policies ----
  adminCreateCustomPolicy: async ({ title, category, body_markdown, ofsted_reference }) => {
    const res = await fetch(`${API_URL}/api/admin/policies/custom`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, category, body_markdown, ofsted_reference }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Create failed (${res.status})`);
    }
    return res.json();
  },
  adminDeletePolicy: async (slug) => {
    const res = await fetch(`${API_URL}/api/admin/policies/${slug}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Delete failed (${res.status})`);
    }
    return res.json();
  },

  // ---- Policy sign-off (Ofsted evidence trail) ----
  getPolicyAcknowledgmentStatus: async () => {
    const res = await fetch(`${API_URL}/api/policies/acknowledgment-status`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to load acknowledgment status');
    return res.json();
  },
  acknowledgePolicies: async (slugsOrAll) => {
    const body = slugsOrAll === 'all' ? { slugs: 'all' } : { slugs: slugsOrAll };
    const res = await fetch(`${API_URL}/api/policies/acknowledge`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Acknowledgment failed (${res.status})`);
    }
    return res.json();
  },
  adminGetAcknowledgmentLog: async () => {
    const res = await fetch(`${API_URL}/api/admin/policies/acknowledgments`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Failed to load acknowledgment log');
    return res.json();
  },

  // ---- Invoices (Phase AC) ----
  listInvoices: async ({ status, parent_user_id, search, limit = 100 } = {}) => {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (parent_user_id) p.set('parent_user_id', parent_user_id);
    if (search) p.set('search', search);
    if (limit) p.set('limit', String(limit));
    const res = await fetch(`${API_URL}/api/admin/invoices?${p.toString()}`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to load invoices');
    return res.json();
  },
  getInvoice: async (invoiceId) => {
    const res = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to load invoice');
    return res.json();
  },
  createInvoice: async (payload) => {
    const res = await fetch(`${API_URL}/api/admin/invoices`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Create invoice failed');
    }
    return res.json();
  },
  updateInvoice: async (invoiceId, payload) => {
    const res = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Update invoice failed');
    }
    return res.json();
  },
  issueInvoice: async (invoiceId, payload = {}) => {
    const res = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}/issue`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Issue invoice failed');
    }
    return res.json();
  },
  markInvoicePaid: async (invoiceId, payload = {}) => {
    const res = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}/mark-paid`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Mark paid failed');
    }
    return res.json();
  },
  voidInvoice: async (invoiceId) => {
    const res = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}/void`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Void failed');
    }
    return res.json();
  },
  previewInvoiceBookings: async ({ parent_user_id, period_start, period_end }) => {
    const res = await fetch(`${API_URL}/api/admin/invoices/preview-bookings`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ parent_user_id, period_start, period_end }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Preview failed');
    }
    return res.json();
  },
  importInvoiceBookings: async (invoiceId, { period_start, period_end, dry_run = false }) => {
    const res = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}/import-bookings`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ period_start, period_end, dry_run }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Import failed');
    }
    return res.json();
  },
  getInvoicePdfUrl: (invoiceId) => {
    const token = getAuthHeader().Authorization?.replace('Bearer ', '');
    // Backend requires Bearer auth on the PDF route; fetch as blob for
    // download so we can attach the header.
    return async () => {
      const res = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}/pdf`, {
        headers: getAuthHeader(),
      });
      if (!res.ok) throw new Error('Failed to download PDF');
      return await res.blob();
    };
  },

  // ---- Recurring schedules (AC.2) ----
  listInvoiceSchedules: async () => {
    const res = await fetch(`${API_URL}/api/admin/invoice-schedules`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to load schedules');
    return res.json();
  },
  createInvoiceSchedule: async (payload) => {
    const res = await fetch(`${API_URL}/api/admin/invoice-schedules`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Create schedule failed'); }
    return res.json();
  },
  updateInvoiceSchedule: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/admin/invoice-schedules/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Update schedule failed'); }
    return res.json();
  },
  deleteInvoiceSchedule: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/invoice-schedules/${id}`, {
      method: 'DELETE', headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Delete failed');
    return res.json();
  },
  tickInvoiceSchedules: async () => {
    const res = await fetch(`${API_URL}/api/admin/invoice-schedules/tick-now`, {
      method: 'POST', headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error('Tick failed');
    return res.json();
  },

  // ---- Bulk run (AC.2) ----
  bulkRunInvoices: async ({ period_start, period_end, dry_run = true, issue_immediately = false, terms_label, notes } = {}) => {
    const res = await fetch(`${API_URL}/api/admin/invoices/bulk-run`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ period_start, period_end, dry_run, issue_immediately, terms_label, notes }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Bulk run failed'); }
    return res.json();
  },

  // ---- Branding studio live preview (AC.2) ----
  previewInvoicePdf: async ({ settings_overrides, show_paid_stamp }) => {
    const res = await fetch(`${API_URL}/api/admin/invoices/preview-pdf`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ settings_overrides, show_paid_stamp }),
    });
    if (!res.ok) throw new Error('Preview failed');
    return await res.blob();
  },

  // ---- Parent portal (AC.2) ----
  parentListInvoices: async () => {
    const res = await fetch(`${API_URL}/api/parent/invoices`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to load invoices');
    return res.json();
  },
  parentGetInvoicePdf: async (invoiceId) => {
    const res = await fetch(`${API_URL}/api/parent/invoices/${invoiceId}/pdf`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to download invoice');
    return await res.blob();
  },
  parentPayInvoiceMollie: async (invoiceId) => {
    const res = await fetch(`${API_URL}/api/parent/invoices/${invoiceId}/pay/mollie`, {
      method: 'POST', headers: getAuthHeader(),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Card payment unavailable'); }
    return res.json();
  },
  parentGetStatementPdf: async ({ date_from, date_to }) => {
    const p = new URLSearchParams({ date_from, date_to });
    const res = await fetch(`${API_URL}/api/parent/statement?${p.toString()}`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to build statement');
    return await res.blob();
  },
  adminGetStatementPdf: async (parentUserId, { date_from, date_to }) => {
    const p = new URLSearchParams({ date_from, date_to });
    const res = await fetch(`${API_URL}/api/admin/invoices/statement/${parentUserId}?${p.toString()}`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to build statement');
    return await res.blob();
  },
  createCreditNote: async (invoiceId, payload) => {
    const res = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}/credit-notes`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Credit note failed'); }
    return res.json();
  },
  listCreditNotesForInvoice: async (invoiceId) => {
    const res = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}/credit-notes`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to load credit notes');
    return res.json();
  },
  getCreditNotePdf: async (creditNoteId) => {
    const res = await fetch(`${API_URL}/api/admin/credit-notes/${creditNoteId}/pdf`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to download credit note');
    return await res.blob();
  },
  tickInvoiceReminders: async () => {
    const res = await fetch(`${API_URL}/api/admin/invoice-reminders/tick-now`, { method: 'POST', headers: getAuthHeader() });
    if (!res.ok) throw new Error('Reminder tick failed');
    return res.json();
  },
  invoiceChaseList: async () => {
    const res = await fetch(`${API_URL}/api/admin/invoice-chase-list`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error('Failed to load chase list');
    return res.json();
  },
  markInvoiceContacted: async (invoiceId, note) => {
    const res = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}/mark-contacted`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note || '' }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Mark contacted failed'); }
    return res.json();
  },

};
